import React, { useEffect, useState, useRef, useMemo } from "react";
import MainLayout from "./MainLayout";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Paper, Button, Box, Typography, Grid, Chip, 
    IconButton, CircularProgress, TableSortLabel, Tooltip,
    Backdrop, LinearProgress, Card, Fade, Portal, Skeleton
} from "@mui/material";

import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import TableChartIcon from "@mui/icons-material/TableChart";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DownloadingIcon from "@mui/icons-material/Downloading";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadDoneIcon from "@mui/icons-material/FileDownloadDone";
import DownloadIcon from "@mui/icons-material/Download";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import * as XLSX from "xlsx";
import { useSelector, useDispatch } from "react-redux";
import Swal from "sweetalert2";
import axios from "axios";
import Apiurl from "../../Helpers/apiUrl";
import FilterBar2 from "./admin_dashboard/FilterBar2";
import { TableBodySkeleton } from "../utils/TableSkeleton";
import { fetchUserOption } from "../../redux/slices/clientSlice";
import { fetchAdminClient, fetchAdminSku, fetchProductOptions } from "../../redux/slices/adminOptionSlice";
import { ClickableTableLink } from "./ClickableTableLink";
import DashboardChart from "../utils/DashboardChart";
import HeatmapChart from "../utils/HeatmapChart";
import Select from "react-select";
import UserService from "../../Services/UserList";

const reactSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        minHeight: '36px',
        height: '36px',
        borderRadius: '6px',
        borderColor: state.isFocused ? '#4f46e5' : 'rgba(0,0,0,0.2)',
        boxShadow: state.isFocused ? '0 0 0 1px #4f46e5' : 'none',
        '&:hover': {
            borderColor: state.isFocused ? '#4f46e5' : 'rgba(0,0,0,0.3)',
        },
        fontSize: '13px',
        backgroundColor: 'white',
        boxSizing: 'border-box'
    }),
    valueContainer: (provided) => ({
        ...provided,
        height: '34px',
        padding: '0 8px',
    }),
    input: (provided) => ({
        ...provided,
        margin: '0px',
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        height: '34px',
    }),
    singleValue: (provided) => ({
        ...provided,
        color: '#1e293b',
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#94a3b8',
    }),
    menu: (provided) => ({
        ...provided,
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        zIndex: 9999
    }),
    menuList: (provided) => ({
        ...provided,
        maxHeight: '180px', // Show ~6-7 values (each item is ~28-30px)
        padding: '4px',
        '::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
        },
        '::-webkit-scrollbar-thumb': {
            background: '#cbd5e1',
            borderRadius: '3px',
        },
        '::-webkit-scrollbar-thumb:hover': {
            background: '#94a3b8',
        }
    }),
    option: (provided, state) => ({
        ...provided,
        borderRadius: '4px',
        fontSize: '12px',
        padding: '6px 10px',
        backgroundColor: state.isSelected 
            ? '#4f46e5' 
            : (state.isFocused ? '#f1f5f9' : 'transparent'),
        color: state.isSelected ? 'white' : '#334155',
        cursor: 'pointer',
        '&:active': {
            backgroundColor: state.isSelected ? '#4f46e5' : '#e2e8f0',
        }
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 99999
    })
};

const AVAILABLE_COLUMNS = [
    { id: 'sku_code', label: 'SKU Code' },
    { id: 'product_desc', label: 'Description' },
    { id: 'category', label: 'Category' },
    { id: 'priceSegment', label: 'Price Segment' },
    { id: 'collection', label: 'Collection' },
    { id: 'brand', label: 'Brand' },
    { id: 'project_id', label: 'Quotation ID' },
    { id: 'draft_id', label: 'Draft ID' },
    { id: 'client_name', label: 'Client Name' },
    { id: 'client_id', label: 'Client ID' },
    { id: 'emp_code', label: 'Employee ID' },
    { id: 'client_region', label: 'Client Region' },
    { id: 'client_state', label: 'Client State' },
    { id: 'user_full_name', label: 'User Name' },
    { id: 'user_region', label: 'User Region' },
    { id: 'user_state', label: 'User State' },
    { id: 'user_channel', label: 'User Channel' },
    { id: 'roleName', label: 'Role' },
    { id: 'qty', label: 'Quantity' },
    { id: 'mrp', label: 'List Price' },
    { id: 'unit', label: 'Offer Price' },
    { id: 'total', label: 'Subtotal' },
    { id: 'total_list_price', label: 'Total List Price' },
    { id: 'created_at', label: 'Created At' },
];

const formatLabel = (str) => {
    if (!str) return "";
    return str.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatAgg = (func) => {
    if (!func) return "";
    const map = {
        sum: "Sum",
        avg: "Average",
        count: "Count",
        min: "Minimum",
        max: "Maximum",
        count_distinct: "Distinct Count"
    };
    return map[func.toLowerCase()] || formatLabel(func);
};

const DEFAULT_COLUMNS = [];

const roleOpt = [
    { label: "IC", value: 2 },
    { label: "BSP", value: 3 },
    { label: "DSP", value: 4 },
    { label: "BD", value: 5 },
    { label: "Sales Enabler", value: 6 },
    { label: "Dealer", value: 7 },
    { label: "Studio", value: 8 },
];

const FY_YEAR_OPTIONS = [
    { label: "2026-2027", value: "2026" },
    { label: "2025-2026", value: "2025" },
    { label: "2024-2025", value: "2024" },
    { label: "2023-2024", value: "2023" },
];

const chartTypeOptions = [
    { value: "bar", label: "Bar Graph (Vertical)" },
    { value: "horizontalBar", label: "Bar Graph (Horizontal)" },
    { value: "line", label: "Line Graph" },
    { value: "pie", label: "Pie Chart" },
    { value: "doughnut", label: "Doughnut Chart" },
    { value: "polarArea", label: "Polar Area Chart" },
    { value: "radar", label: "Radar Chart" },
    { value: "heatmap", label: "Heatmap Grid" },
    { value: "table", label: "Data Table" },
    { value: "card", label: "Number Card" }
];

const chartWidthOptions = [
    { value: "half", label: "Half Row (50% Width)" },
    { value: "full", label: "Full Row (100% Width)" }
];

const filterColumnOptions = [
    { value: 'roleName', label: 'Role Name' },
    { value: 'user_full_name', label: 'User Name' },
    { value: 'user_region', label: 'User Region' },
    { value: 'user_state', label: 'User State' },
    { value: 'user_channel', label: 'User Channel' },
    { value: 'client_name', label: 'Client Name' },
    { value: 'client_region', label: 'Client Region' },
    { value: 'client_state', label: 'Client State' },
    { value: 'sku_code', label: 'SKU Code' },
    { value: 'category', label: 'Category' },
    { value: 'brand', label: 'Brand' },
    { value: 'collection', label: 'Collection' }
].sort((a, b) => a.label.localeCompare(b.label));

const filterOperatorOptions = [
    { value: 'equals', label: 'Equals (=)' },
    { value: 'not_equals', label: 'Not Equals (≠)' },
    { value: 'contains', label: 'Contains' },
    { value: 'in_list', label: 'In List (comma-separated)' },
    { value: 'not_in_list', label: 'Not In List (comma-separated)' }
];

const rowColOptions = AVAILABLE_COLUMNS.filter(c => !['qty','mrp','unit','total','total_list_price','created_at'].includes(c.id)).map(c => ({ value: c.id, label: c.label }))
    .sort((a, b) => a.label.localeCompare(b.label));

const colColOptions = [
    ...AVAILABLE_COLUMNS.filter(c => !['sku_code','product_desc','project_id','client_name','roleName','qty','mrp','unit','total','total_list_price'].includes(c.id)).map(c => ({ value: c.id, label: c.label })),
    { value: "created_at_day", label: "Created Date (Day Wise)" },
    { value: "created_at_month", label: "Created Month (Month Wise)" },
    { value: "created_at_year", label: "Created Year (Year Wise)" }
].sort((a, b) => a.label.localeCompare(b.label));

const valColOptions = AVAILABLE_COLUMNS.map(c => ({ value: c.id, label: c.label }))
    .sort((a, b) => a.label.localeCompare(b.label));

const aggregateOptions = [
    { value: "sum", label: "Sum (Total)" },
    { value: "count", label: "Count (Number of Records)" },
    { value: "avg", label: "Average" },
    { value: "min", label: "Minimum Value" },
    { value: "max", label: "Maximum Value" },
    { value: "count_distinct", label: "Count Distinct (Unique Values)" }
];

const xAxisOptions = [
    ...AVAILABLE_COLUMNS.filter(c => !['qty','mrp','unit','total','total_list_price'].includes(c.id)).map(c => ({ value: c.id, label: c.label })),
    { value: "created_at_day", label: "Created Date (Day Wise)" },
    { value: "created_at_month", label: "Created Month (Month Wise)" },
    { value: "created_at_year", label: "Created Year (Year Wise)" }
].sort((a, b) => a.label.localeCompare(b.label));

const yAxisOptions = AVAILABLE_COLUMNS.map(c => ({ value: c.id, label: c.label }))
    .sort((a, b) => a.label.localeCompare(b.label));



const AdminDynamicDashboard = () => {
    const dispatch = useDispatch();
    
    // Helper to load stored filter default value
    const getStoredFilter = (key, fallback) => {
        return fallback;
    };

    // Saved dashboards popover/menu
    const [savedDashboards, setSavedDashboards] = useState([]);
    const [selectedDashboardId, setSelectedDashboardId] = useState("");
    
    // Default view name label (loads from local storage)
    const [defaultViewName, setDefaultViewName] = useState(() => 
        localStorage.getItem("default_view_name") || "Default View (Blank Table)"
    );
    
    // Columns & Data (Loads default saved layout if it exists)
    const [selectedColumns, setSelectedColumns] = useState(() => {
        try {
            const stored = localStorage.getItem("default_view_columns");
            return stored ? JSON.parse(stored) : DEFAULT_COLUMNS;
        } catch (e) {
            return DEFAULT_COLUMNS;
        }
    });
    const [tableData, setTableData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [extractedCount, setExtractedCount] = useState(0);
    const [estimatedTime, setEstimatedTime] = useState("Calculating...");
    const [startTime, setStartTime] = useState(null);
    const [downloadTotal, setDownloadTotal] = useState(0);
    const downloadAbortControllerRef = React.useRef(null);
    const [draggedIndex, setDraggedIndex] = useState(null);
    
    // Dynamic Chart builder states
    const cachedCharts = localStorage.getItem("default_view_charts");
    const [charts, setCharts] = useState(() => {
        try {
            return cachedCharts ? JSON.parse(cachedCharts) : [];
        } catch (e) {
            return [];
        }
    });

    
    const cachedPos = localStorage.getItem("default_view_table_position");
    const [tablePosition, setTablePosition] = useState(cachedPos || 'bottom');
    
    // Unified layout state supporting ordering of custom cards
    const [layout, setLayout] = useState(() => {
        const cachedLayout = localStorage.getItem("default_view_layout_order");
        if (cachedLayout) {
            try {
                const parsed = JSON.parse(cachedLayout);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    return parsed.filter(id => id !== 'table');
                }
            } catch (e) {
                console.error("Error parsing default_view_layout_order", e);
            }
        }
        const cachedCharts = localStorage.getItem("default_view_charts");
        let defaultCharts = [];
        try {
            defaultCharts = cachedCharts ? JSON.parse(cachedCharts) : [];
        } catch (e) {
            defaultCharts = [];
        }
        return defaultCharts.map(c => c.id);
    });
    const [draggingLayoutIndex, setDraggingLayoutIndex] = useState(null);
    const draggedLayoutIndexRef = useRef(null);
    
    const [isChartModalOpen, setIsChartModalOpen] = useState(false);
    const [editingChart, setEditingChart] = useState(null);
    const [draggingChartIndex, setDraggingChartIndex] = useState(null);
    const [draggedBlock, setDraggedBlock] = useState(null);
    const [isViewSelectorOpen, setIsViewSelectorOpen] = useState(false);
    const viewSelectorRef = useRef(null);

    // Chart Modal Fields
    const [chartTitle, setChartTitle] = useState("Custom Chart");
    const [chartType, setChartType] = useState("bar");
    const [chartXAxis, setChartXAxis] = useState("category");
    const [chartYAxis, setChartYAxis] = useState("total");
    const [chartRowCol, setChartRowCol] = useState("roleName");
    const [chartColCol, setChartColCol] = useState("user_state");
    const [chartValCol, setChartValCol] = useState("total");
    const [chartFunc, setChartFunc] = useState("sum");
    const [chartWidth, setChartWidth] = useState("half");
    const [chartTableColumns, setChartTableColumns] = useState([]);
    const [chartFilters, setChartFilters] = useState([]);
    const [modalTab, setModalTab] = useState('general');
    const [chartComparisonEnabled, setChartComparisonEnabled] = useState(false);
    const [chartComparisonPeriod, setChartComparisonPeriod] = useState('month');
    const [chartConditionalRules, setChartConditionalRules] = useState([]);
    const [chartSecondaryYAxisEnabled, setChartSecondaryYAxisEnabled] = useState(false);
    const [chartSecondaryYAxisCol, setChartSecondaryYAxisCol] = useState('project_id');
    const [chartSecondaryYAxisFunc, setChartSecondaryYAxisFunc] = useState('count_distinct');
    
    // Pagination & Sorting (Default 15 rows to fit height perfectly)
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [sortBy, setSortBy] = useState({ sort_by: "created_at", order: "desc" });

    // Filter states - loading from localStorage if on default view
    const [searchTerm, setSearchTerm] = useState(() => getStoredFilter("search", ""));
    const [selectedUsers, setSelectedUsers] = useState(() => getStoredFilter("selectedUsers", []));
    const [selectedClients, setSelectedClients] = useState(() => getStoredFilter("selectedClients", []));
    const [selectedCollection, setSelectedCollection] = useState(() => getStoredFilter("selectedCollection", []));
    const [selectedSegment, setSelectedSegment] = useState(() => getStoredFilter("selectedSegment", []));
    const [selectedCategory, setSelectedCategory] = useState(() => getStoredFilter("selectedCategory", []));
    const [roleId, setRoleId] = useState(() => getStoredFilter("roleId", []));
    const [region, setRegion] = useState(() => getStoredFilter("region", []));
    const [state, setState] = useState(() => getStoredFilter("state", []));
    const [selectedFromDate, setSelectedFromDate] = useState(null);
    const [selectedToDate, setSelectedToDate] = useState(null);
    const [sku, setSku] = useState(() => getStoredFilter("sku", []));
    const [selectedBrands, setSelectedBrands] = useState(() => getStoredFilter("selectedBrands", []));
    const [channelOpt, setChannelOpt] = useState([]);
    const [selectedChannels, setSelectedChannels] = useState(() => getStoredFilter("selectedChannels", []));

    const [month, setMonth] = useState(() => {
        const storedMonths = getStoredFilter("month", null);
        if (storedMonths) {
            return storedMonths.split(",").map(Number);
        }
        const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
        const currentCalendarMonth = new Date().getMonth() + 1;
        const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
        return fullFYMonths.slice(0, currentMonthIndex + 1);
    });
    const [year, setYear] = useState(() => {
        const storedYear = getStoredFilter("year", null);
        if (storedYear) {
            return FY_YEAR_OPTIONS.filter(y => y.value === storedYear);
        }
        const currentYrStr = String(new Date().getFullYear());
        return FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr);
    });

    const { userOption } = useSelector((state) => state.client);
    const { clientOpt, segmentOpt, collectionOpt, categoryOpt, regionOpt, stateOpt, skuOpt } = useSelector((state) => state.adminOption);

    const userOpt = userOption?.map(i => ({ value: i.id, label: `${i.name} ${i.last_name}` })) || [];

    useEffect(() => {
        dispatch(fetchUserOption());
        dispatch(fetchAdminClient());
        dispatch(fetchProductOptions());
        dispatch(fetchAdminSku());
        fetchSavedDashboards();
        UserService.GetChannels().then(response => {
            if (response && response.data) {
                setChannelOpt(response.data.map(ch => ({
                    value: ch.id,
                    label: ch.channel_name
                })));
            }
        }).catch(console.error);
    }, [dispatch]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (viewSelectorRef.current && !viewSelectorRef.current.contains(event.target)) {
                setIsViewSelectorOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);



    const fetchSavedDashboards = async () => {
        try {
            const token = localStorage.getItem("u_token");
            const response = await axios.get(`${Apiurl}admin/dynamic-dashboard/list`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.status) {
                setSavedDashboards(response.data.data);
                
                // Automatically restore last active custom view on page reload has been removed to ensure refresh starts clean.
            }
        } catch (error) {
            console.error("Error fetching saved layouts", error);
        }
    };

    const hasActiveFilters = 
        year.length > 0 || 
        month.length > 0 || 
        selectedUsers.length > 0 || 
        selectedClients.length > 0 || 
        selectedChannels.length > 0 || 
        sku.length > 0 || 
        selectedCategory.length > 0 || 
        selectedSegment.length > 0 || 
        selectedCollection.length > 0 || 
        roleId.length > 0 || 
        region.length > 0 || 
        state.length > 0 || 
        selectedFromDate !== null || 
        selectedToDate !== null || 
        searchTerm !== "";

    const formatDateValue = (val) => {
        if (!val) return null;
        if (typeof val === 'string') return val;
        if (typeof val.format === 'function') return val.format("YYYY-MM-DD");
        return null;
    };

    const currentFilters = useMemo(() => ({
        user: selectedUsers.map(x => x.value).join(","),
        client: selectedClients.map(x => x.value).join(","),
        category: selectedCategory.map(x => x.value).join(","),
        segment: selectedSegment.map(x => x.value).join(","),
        collection: selectedCollection.map(x => x.value).join(","),
        brand: selectedBrands.map(x => x.value).join(","),
        role: roleId.map(x => x.value).join(","),
        channels: selectedChannels.map(x => x.value).join(","),
        region: region.map(x => x.label).join(","),
        state: state.map(x => x.label).join(","),
        year: year.map(x => x.value).join(","),
        month: month.join(","),
        sku: sku.map(x => x.value).join(","),
        fromDate: formatDateValue(selectedFromDate),
        toDate: formatDateValue(selectedToDate),
        search: searchTerm
    }), [selectedUsers, selectedClients, selectedCategory, selectedSegment, 
         selectedCollection, selectedBrands, roleId, region, state, year, month, 
         selectedFromDate, selectedToDate, searchTerm, sku, selectedChannels]);





    const handleColumnToggle = (columnId) => {
        setPage(0);
        setSelectedColumns(prev => {
            if (prev.includes(columnId)) {
                if (prev.length <= 1) {
                    return [];
                }
                return prev.filter(id => id !== columnId);
            } else {
                return [...prev, columnId];
            }
        });
    };

    // Smooth HTML5 Drag & Drop handlers with real-time feedback
    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
    };

    const handleDragEnter = (e, index) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        const nextCols = [...selectedColumns];
        const [draggedItem] = nextCols.splice(draggedIndex, 1);
        nextCols.splice(index, 0, draggedItem);

        setSelectedColumns(nextCols);
        setDraggedIndex(index);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDraggedIndex(null);
    };

    const handleLayoutDragStart = (e, index) => {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.closest('.MuiTablePagination-root') || e.target.closest('.MuiTableCell-root') || e.target.closest('button')) {
            e.preventDefault();
            return;
        }
        setDraggingLayoutIndex(index);
        draggedLayoutIndexRef.current = index;
        e.dataTransfer.effectAllowed = "move";
    };

    const handleLayoutDragOver = (e, index) => {
        e.preventDefault();
        if (draggedLayoutIndexRef.current === null || draggedLayoutIndexRef.current === index) return;

        const sourceIndex = draggedLayoutIndexRef.current;
        setLayout(prev => {
            const next = [...prev];
            const [draggedItem] = next.splice(sourceIndex, 1);
            next.splice(index, 0, draggedItem);
            return next;
        });
        
        draggedLayoutIndexRef.current = index;
        setDraggingLayoutIndex(index);
    };

    const handleLayoutDragEnd = () => {
        setDraggingLayoutIndex(null);
        draggedLayoutIndexRef.current = null;
        autoSaveLayout(charts, selectedColumns, layout);
    };

    const handleLoadDashboard = (dashboard) => {
        setSelectedDashboardId(dashboard.id);
        localStorage.setItem("active_dashboard_id", dashboard.id);
        try {
            const cols = JSON.parse(dashboard.columns);
            setSelectedColumns(cols);
        } catch (e) {
            console.error("Error parsing dashboard columns", e);
        }
        
        if (dashboard.filters) {
            try {
                const flt = JSON.parse(dashboard.filters);
                // Reset active filters to default configuration when switching dashboards
                setSearchTerm("");
                setSelectedFromDate(null);
                setSelectedToDate(null);
                
                const loadedCharts = flt.charts || [];
                if (flt.charts) setCharts(loadedCharts);
                else setCharts([]);
                if (flt.tablePosition) setTablePosition(flt.tablePosition);
                else setTablePosition('bottom');

                if (flt.layoutOrder && Array.isArray(flt.layoutOrder)) {
                    setLayout(flt.layoutOrder.filter(id => id !== 'table'));
                } else {
                    setLayout(loadedCharts.map(c => c.id));
                }

                setSelectedUsers([]);
                setSelectedClients([]);
                setSelectedCategory([]);
                setSelectedSegment([]);
                setSelectedCollection([]);
                setRoleId([]);
                setRegion([]);
                setState([]);
                setSku([]);

                const currentYrStr = String(new Date().getFullYear());
                setYear(FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr));

                const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
                const currentCalendarMonth = new Date().getMonth() + 1;
                const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
                setMonth(fullFYMonths.slice(0, currentMonthIndex + 1));
            } catch (e) {
                console.error("Error parsing filters", e);
                setCharts([]);
                setTablePosition('bottom');
            }
        } else {
            setCharts([]);
            setTablePosition('bottom');
        }
    };



    const createNewBlankView = async (name) => {
        try {
            const token = localStorage.getItem("u_token");
            const response = await axios.post(`${Apiurl}admin/dynamic-dashboard/save`, {
                name,
                columns: [], // Start completely blank
                filters: {
                    search: "",
                    fromDate: null,
                    toDate: null,
                    charts: [],
                    tablePosition: 'bottom'
                }
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                Swal.fire("Success", `Created new blank dashboard "${name}" successfully. Customize it and click Save to save your layout.`, "success");
                
                const listResp = await axios.get(`${Apiurl}admin/dynamic-dashboard/list`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (listResp.data.status) {
                    setSavedDashboards(listResp.data.data);
                    const newDash = listResp.data.data.find(d => d.name && d.name.toLowerCase() === name.toLowerCase());
                    if (newDash) {
                        handleLoadDashboard(newDash);
                    }
                }
            }
        } catch (error) {
            console.error("Error creating blank view", error);
            Swal.fire("Error", "Failed to create new view.", "error");
        }
    };

    const handleUpdateCustomView = async (id, name) => {
        try {
            const token = localStorage.getItem("u_token");
            const response = await axios.post(`${Apiurl}admin/dynamic-dashboard/save`, {
                name,
                columns: selectedColumns,
                filters: {
                    search: searchTerm,
                    fromDate: selectedFromDate ? (typeof selectedFromDate === 'string' ? selectedFromDate : (typeof selectedFromDate.format === 'function' ? selectedFromDate.format("YYYY-MM-DD") : null)) : null,
                    toDate: selectedToDate ? (typeof selectedToDate === 'string' ? selectedToDate : (typeof selectedToDate.format === 'function' ? selectedToDate.format("YYYY-MM-DD") : null)) : null,
                    charts: charts,
                    tablePosition: layout[0] === 'table' ? 'top' : 'bottom',
                    layoutOrder: layout,
                    selectedUsers,
                    selectedClients,
                    selectedCategory,
                    selectedSegment,
                    selectedCollection,
                    roleId,
                    region,
                    state,
                    year,
                    month,
                    sku
                }
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                Swal.fire("Saved", "Dashboard view updated successfully.", "success");
                fetchSavedDashboards();
            }
        } catch (error) {
            console.error("Error updating view", error);
            Swal.fire("Error", "Failed to update dashboard view.", "error");
        }
    };

    const handleSaveDefaultConfig = () => {
        try {
            localStorage.setItem("default_view_columns", JSON.stringify(selectedColumns));
            localStorage.setItem("default_view_charts", JSON.stringify(charts));
            localStorage.setItem("default_view_table_position", layout[0] === 'table' ? 'top' : 'bottom');
            localStorage.setItem("default_view_layout_order", JSON.stringify(layout));
            const filtersToSave = {
                search: searchTerm,
                year: year.map(x => x.value).join(","),
                month: month.join(","),
                selectedUsers,
                selectedClients,
                selectedCategory,
                selectedSegment,
                selectedCollection,
                selectedChannels,
                roleId,
                region,
                state,
                sku
            };
            localStorage.setItem("default_view_filters", JSON.stringify(filtersToSave));
            Swal.fire("Saved", "Default view layout saved successfully.", "success");
        } catch (e) {
            console.error("Error saving default view layout", e);
            Swal.fire("Error", "Failed to save default view layout.", "error");
        }
    };
    const autoSaveLayout = async (updatedCharts, updatedColumns, updatedLayout) => {
        const isCustom = selectedDashboardId !== "" && selectedDashboardId !== "new_blank";
        const targetCharts = updatedCharts !== undefined ? updatedCharts : charts;
        const targetCols = updatedColumns !== undefined ? updatedColumns : selectedColumns;
        const targetLayout = updatedLayout !== undefined ? updatedLayout : layout;
        
        if (isCustom) {
            try {
                const token = localStorage.getItem("u_token");
                const activeDash = savedDashboards.find(d => d.id === selectedDashboardId);
                const dashName = activeDash ? activeDash.name : "Custom View";
                
                await axios.post(`${Apiurl}admin/dynamic-dashboard/save`, {
                    name: dashName,
                    columns: targetCols,
                    filters: {
                        search: searchTerm,
                        fromDate: selectedFromDate ? (typeof selectedFromDate === 'string' ? selectedFromDate : (typeof selectedFromDate.format === 'function' ? selectedFromDate.format("YYYY-MM-DD") : null)) : null,
                        toDate: selectedToDate ? (typeof selectedToDate === 'string' ? selectedToDate : (typeof selectedToDate.format === 'function' ? selectedToDate.format("YYYY-MM-DD") : null)) : null,
                        charts: targetCharts,
                        tablePosition: targetLayout[0] === 'table' ? 'top' : 'bottom',
                        layoutOrder: targetLayout,
                        selectedUsers,
                        selectedClients,
                        selectedCategory,
                        selectedSegment,
                        selectedCollection,
                        roleId,
                        region,
                        state,
                        year,
                        month,
                        sku
                    }
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                fetchSavedDashboards();
            } catch (e) {
                console.error("Error auto-saving custom view", e);
            }
        } else {
            try {
                localStorage.setItem("default_view_columns", JSON.stringify(targetCols));
                localStorage.setItem("default_view_charts", JSON.stringify(targetCharts));
                localStorage.setItem("default_view_table_position", targetLayout[0] === 'table' ? 'top' : 'bottom');
                localStorage.setItem("default_view_layout_order", JSON.stringify(targetLayout));
                
                // Filters are no longer persisted to localStorage to ensure refresh resets all filters.
            } catch (e) {
                console.error("Error auto-saving default view", e);
            }
        }
    };

    const promptRenameDefault = () => {
        Swal.fire({
            title: 'Rename Default View Label',
            input: 'text',
            inputValue: defaultViewName,
            inputPlaceholder: 'Enter label name...',
            showCancelButton: true,
            confirmButtonText: 'Rename',
            confirmButtonColor: '#38bdf8',
            cancelButtonColor: '#ef4444',
            inputValidator: (value) => {
                if (!value) {
                    return 'Name cannot be empty!';
                }
            }
        }).then((result) => {
            if (result.isConfirmed) {
                const name = result.value.trim();
                localStorage.setItem("default_view_name", name);
                setDefaultViewName(name);
                // Sync current columns to local storage default to ensure name and data are in sync
                localStorage.setItem("default_view_columns", JSON.stringify(selectedColumns));
                Swal.fire("Renamed & Saved", "Default view label updated successfully.", "success");
            }
        });
    };

    const promptRenameCustom = (id, currentName) => {
        Swal.fire({
            title: 'Rename Custom View',
            input: 'text',
            inputValue: currentName,
            inputPlaceholder: 'Enter new name...',
            showCancelButton: true,
            confirmButtonText: 'Rename',
            confirmButtonColor: '#38bdf8',
            cancelButtonColor: '#ef4444',
            inputValidator: (value) => {
                if (!value) {
                    return 'Name cannot be empty!';
                }
            }
        }).then(async (result) => {
            if (result.isConfirmed) {
                const name = result.value.trim();
                try {
                    const token = localStorage.getItem("u_token");
                    const response = await axios.post(`${Apiurl}admin/dynamic-dashboard/rename/${id}`, {
                        name
                    }, {
                        headers: { Authorization: `Bearer ${token}` }
                    });

                    if (response.data.status) {
                        Swal.fire("Success", response.data.message, "success");
                        fetchSavedDashboards();
                    }
                } catch (error) {
                    console.error("Error renaming view", error);
                    const msg = error.response?.data?.message || "Failed to rename view.";
                    Swal.fire("Error", msg, "error");
                }
            }
        });
    };

    const promptNewViewSave = () => {
        Swal.fire({
            title: 'Create New View',
            input: 'text',
            inputPlaceholder: 'Enter view name...',
            showCancelButton: true,
            confirmButtonText: 'Create',
            confirmButtonColor: '#38bdf8',
            cancelButtonColor: '#ef4444',
            inputValidator: (value) => {
                if (!value) {
                    return 'You need to write a name!';
                }
            }
        }).then((result) => {
            if (result.isConfirmed) {
                const name = result.value.trim();
                const existing = savedDashboards.find(d => d.name && d.name.toLowerCase() === name.toLowerCase());
                if (existing) {
                    Swal.fire("Warning", `A saved view named "${name}" already exists. Please choose a different name.`, "warning");
                } else {
                    createNewBlankView(name);
                }
            }
        });
    };

    const handleOpenChartModal = (chart = null) => {
        setDraggingChartIndex(null);
        setDraggedBlock(null);
        setModalTab('general');
        if (chart) {
            setEditingChart(chart);
            setChartTitle(chart.title || "Custom Chart");
            setChartType(chart.type || "bar");
            setChartXAxis(chart.xAxisColumn || "category");
            setChartYAxis(chart.yAxisColumn || "total");
            setChartRowCol(chart.rowColumn || "roleName");
            setChartColCol(chart.colColumn || "user_state");
            setChartValCol(chart.valColumn || "total");
            setChartFunc(chart.aggregateFunction || "sum");
            setChartWidth(chart.size || "half");
            setChartTableColumns(chart.columns || []);
            setChartFilters(chart.filters || []);
            setChartComparisonEnabled(chart.comparisonEnabled || false);
            setChartComparisonPeriod(chart.comparisonPeriod || 'month');
            setChartConditionalRules(chart.conditionalRules || []);
            setChartSecondaryYAxisEnabled(chart.secondaryYAxisEnabled || false);
            setChartSecondaryYAxisCol(chart.secondaryYAxisCol || 'project_id');
            setChartSecondaryYAxisFunc(chart.secondaryYAxisFunc || 'count_distinct');
        } else {
            setEditingChart(null);
            setChartTitle("Custom Chart");
            setChartType("bar");
            setChartXAxis("category");
            setChartYAxis("total");
            setChartRowCol("roleName");
            setChartColCol("user_state");
            setChartValCol("total");
            setChartFunc("sum");
            setChartWidth("half");
            setChartTableColumns([]);
            setChartFilters([]);
            setChartComparisonEnabled(false);
            setChartComparisonPeriod('month');
            setChartConditionalRules([]);
            setChartSecondaryYAxisEnabled(false);
            setChartSecondaryYAxisCol('project_id');
            setChartSecondaryYAxisFunc('count_distinct');
        }
        setIsChartModalOpen(true);
    };

    const handleSaveChart = () => {
        let finalTitle = chartTitle;
        if (!finalTitle || finalTitle.trim() === "" || finalTitle.toLowerCase() === "custom chart") {
            if (chartType === 'heatmap') {
                const valLabel = formatLabel(chartValCol);
                const rowLabel = formatLabel(chartRowCol);
                const colLabel = formatLabel(chartColCol);
                const funcLabel = formatAgg(chartFunc);
                finalTitle = `${valLabel} by ${rowLabel} & ${colLabel} (${funcLabel})`;
            } else if (chartType === 'table') {
                finalTitle = `Custom Table (${chartTableColumns.length} cols)`;
            } else {
                const yLabel = formatLabel(chartYAxis);
                const xLabel = formatLabel(chartXAxis);
                const funcLabel = formatAgg(chartFunc);
                finalTitle = `${yLabel} by ${xLabel} (${funcLabel})`;
            }
        }

        const newChart = {
            id: editingChart ? editingChart.id : `chart_${Date.now()}`,
            title: finalTitle,
            type: chartType,
            aggregateFunction: chartFunc,
            size: chartWidth,
            filters: chartFilters.filter(f => f.column && f.value.trim() !== ''),
            comparisonEnabled: chartType === 'card' ? chartComparisonEnabled : false,
            comparisonPeriod: chartType === 'card' ? chartComparisonPeriod : 'month',
            conditionalRules: (chartType === 'table' || chartType === 'heatmap') ? chartConditionalRules : [],
            secondaryYAxisEnabled: ['bar', 'horizontalBar', 'line'].includes(chartType) ? chartSecondaryYAxisEnabled : false,
            secondaryYAxisCol: chartSecondaryYAxisCol,
            secondaryYAxisFunc: chartSecondaryYAxisFunc,
            ...(chartType === 'heatmap' ? {
                rowColumn: chartRowCol,
                colColumn: chartColCol,
                valColumn: chartValCol
            } : chartType === 'table' ? {
                columns: chartTableColumns
            } : {
                xAxisColumn: chartXAxis,
                yAxisColumn: chartYAxis
            })
        };

        setCharts(prev => {
            let next;
            if (editingChart) {
                next = prev.map(c => c.id === editingChart.id ? newChart : c);
                autoSaveLayout(next, selectedColumns, layout);
            } else {
                next = [...prev, newChart];
                const nextLayout = [...layout, newChart.id];
                setLayout(nextLayout);
                autoSaveLayout(next, selectedColumns, nextLayout);
            }
            return next;
        });



        setIsChartModalOpen(false);
        setEditingChart(null);
    };

    const handleDeleteChart = (chartId) => {
        setDraggingChartIndex(null);
        setDraggedBlock(null);
        const nextCharts = charts.filter(c => c.id !== chartId);
        const nextLayout = layout.filter(id => id !== chartId);
        setCharts(nextCharts);
        setLayout(nextLayout);
        autoSaveLayout(nextCharts, selectedColumns, nextLayout);
    };

    const handleUpdateChartColumns = (chartId, newColumns) => {
        setCharts(prev => {
            const next = prev.map(c => c.id === chartId ? { ...c, columns: newColumns } : c);
            autoSaveLayout(next, selectedColumns, layout);
            return next;
        });
    };

    const handleChartDragStart = (e, index) => {
        setDraggingChartIndex(index);
        e.dataTransfer.effectAllowed = "move";
    };

    const handleChartDragOver = (e, index) => {
        e.preventDefault();
        if (draggingChartIndex === null || draggingChartIndex === index) return;
        
        setCharts(prev => {
            const next = [...prev];
            const temp = next[draggingChartIndex];
            next[draggingChartIndex] = next[index];
            next[index] = temp;
            return next;
        });
        setDraggingChartIndex(index);
    };

    const handleChartDragEnd = () => {
        setDraggingChartIndex(null);
        setDraggedBlock(null);
    };

    const handleRenameActiveView = () => {
        if (selectedDashboardId === "" || selectedDashboardId === "new_blank") {
            promptRenameDefault();
        } else {
            const currentView = savedDashboards.find(d => String(d.id) === String(selectedDashboardId));
            if (currentView) {
                promptRenameCustom(selectedDashboardId, currentView.name);
            }
        }
    };

    const handleDeleteDashboardById = (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "Do you want to delete this saved layout?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ef4444",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: 'Deleting view...',
                    allowOutsideClick: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                try {
                    const token = localStorage.getItem("u_token");
                    await axios.delete(`${Apiurl}admin/dynamic-dashboard/delete/${id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    
                    Swal.fire("Deleted!", "Your custom dashboard view has been deleted.", "success");
                    
                    if (selectedDashboardId === id) {
                        handleViewChange("");
                    }
                    fetchSavedDashboards();
                } catch (error) {
                    console.error("Error deleting dashboard view", error);
                    Swal.fire("Error", "Failed to delete saved view.", "error");
                }
            }
        });
    };

    // Calculate estimated time for progressive download
    useEffect(() => {
        if (isDownloading && startTime && extractedCount > 0 && downloadTotal > 0) {
            const elapsedSeconds = (Date.now() - startTime) / 1000;
            const speed = extractedCount / elapsedSeconds;

            if (speed > 0) {
                const remaining = downloadTotal - extractedCount;
                const secondsRemaining = Math.ceil(remaining / speed);

                if (secondsRemaining <= 0) {
                    setEstimatedTime("Finishing up...");
                } else if (secondsRemaining < 60) {
                    setEstimatedTime(`${secondsRemaining} seconds`);
                } else if (secondsRemaining < 3600) {
                    setEstimatedTime(`${Math.ceil(secondsRemaining / 60)} minutes`);
                } else {
                    setEstimatedTime(`${Math.ceil(secondsRemaining / 3600)} hours`);
                }
            }
        }
    }, [isDownloading, startTime, extractedCount, downloadTotal]);

    const handleCancelDownload = () => {
        if (downloadAbortControllerRef.current) {
            downloadAbortControllerRef.current.abort();
            setIsDownloading(false);
            setProgress(0);
            setExtractedCount(0);
            setEstimatedTime("Calculating...");
            setStartTime(null);

            Swal.fire({
                title: "Export Cancelled",
                text: "The export process has been stopped.",
                icon: "info",
                timer: 2000,
                showConfirmButton: false,
            });
        }
    };

    const handleDownloadExcel = async () => {
        if (totalRecords === 0) {
            Swal.fire({
                title: "No Data Available",
                text: "There is no data to export based on current filters.",
                icon: "info",
                confirmButtonColor: "#38bdf8",
            });
            return;
        }

        setIsDownloading(true);
        setProgress(0);
        setExtractedCount(0);
        setDownloadTotal(0);
        setEstimatedTime("Calculating...");
        setStartTime(Date.now());
        downloadAbortControllerRef.current = new AbortController();

        const LIMIT = 5000;
        let allDownloadedData = [];
        const token = localStorage.getItem("u_token");

        try {
            let page = 1;
            let hasMore = true;

            while (hasMore) {
                const response = await axios.post(
                    `${Apiurl}admin/dynamic-dashboard/data`,
                    {
                        columns: selectedColumns,
                        page: page,
                        limit: LIMIT,
                        sort_by: sortBy.sort_by,
                        sort_order: sortBy.order,
                        ...currentFilters
                    },
                    {
                        signal: downloadAbortControllerRef.current.signal,
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );

                const pageData = response.data?.data?.data || [];
                const returnedTotal = response.data?.data?.total || 0;

                if (page === 1) {
                    setDownloadTotal(returnedTotal);
                }

                allDownloadedData = [...allDownloadedData, ...pageData];
                setExtractedCount(allDownloadedData.length);

                if (returnedTotal > 0) {
                    setProgress(Math.min(99, Math.round((allDownloadedData.length / returnedTotal) * 100)));
                }

                if (pageData.length < LIMIT || allDownloadedData.length >= returnedTotal) {
                    hasMore = false;
                } else {
                    page++;
                }
            }

            const headings = selectedColumns.map(colId => {
                const col = AVAILABLE_COLUMNS.find(c => c.id === colId);
                return col ? col.label : colId;
            });

            const sheetData = [
                ["SL No.", ...headings],
                ...allDownloadedData.map((row, idx) => [
                    idx + 1,
                    ...selectedColumns.map(colId => {
                        if (['mrp', 'unit', 'total', 'total_list_price'].includes(colId)) {
                            return row[colId] !== undefined && row[colId] !== null ? Number(row[colId]) : 0;
                        }
                        if (colId === 'created_at') {
                            return row.created_at ? new Date(row.created_at).toLocaleString("en-GB") : "N/A";
                        }
                        return row[colId] || "N/A";
                    })
                ])
            ];

            const ws = XLSX.utils.aoa_to_sheet(sheetData);
            const wscols = [{ wch: 8 }, ...selectedColumns.map(() => ({ wch: 20 }))];
            ws["!cols"] = wscols;

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Dynamic Report");

            const timestamp = new Date().toISOString().split("T")[0];
            const fileName = `Dynamic_Report_${timestamp}.xlsx`;
            XLSX.writeFile(wb, fileName);

            Swal.fire({
                title: "Export Successful!",
                html: `
                  <div style="text-align: left; padding: 10px;">
                    <p><strong>Records exported:</strong> ${allDownloadedData.length.toLocaleString()}</p>
                    <p><strong>File name:</strong> ${fileName}</p>
                  </div>
                `,
                icon: "success",
                confirmButtonColor: "#10b981",
                confirmButtonText: "OK",
            });
        } catch (error) {
            if (!axios.isCancel(error)) {
                console.error("Error exporting data", error);
                Swal.fire({
                    title: "Export Failed",
                    text: error.message || "Failed to fetch data for export. Please try again.",
                    icon: "error",
                    confirmButtonColor: "#ef4444",
                });
            }
        } finally {
            setIsDownloading(false);
            setProgress(0);
            setDownloadTotal(0);
            setEstimatedTime("Calculating...");
            setStartTime(null);
        }
    };

    const handleDownloadChartData = async (chart) => {
        setIsDownloading(true);
        setProgress(0);
        setExtractedCount(0);
        setDownloadTotal(0);
        setEstimatedTime("Calculating...");
        setStartTime(Date.now());
        downloadAbortControllerRef.current = new AbortController();

        const LIMIT = 5000;
        let allDownloadedData = [];
        const token = localStorage.getItem("u_token");

        let colsToFetch = [];
        
        const isDraftCard = chart.yAxisColumn === 'draft_id' || chart.xAxisColumn === 'draft_id';
        const isQuotationCard = chart.yAxisColumn === 'project_id' || chart.xAxisColumn === 'project_id';
        const isClientCard = chart.yAxisColumn === 'client_name' || chart.yAxisColumn === 'client_id' || chart.xAxisColumn === 'client_name' || chart.xAxisColumn === 'client_id';

        if (chart.type === 'card') {
            if (isDraftCard) {
                colsToFetch = [
                    'draft_id',
                    'user_full_name',
                    'user_state',
                    'user_region',
                    'user_channel',
                    'roleName',
                    'emp_code',
                    'client_name',
                    'client_state',
                    'client_region',
                    'client_id',
                    'total',
                    'created_at'
                ];
            } else if (isQuotationCard) {
                colsToFetch = [
                    'project_id',
                    'user_full_name',
                    'user_state',
                    'user_region',
                    'user_channel',
                    'roleName',
                    'emp_code',
                    'client_name',
                    'client_state',
                    'client_region',
                    'client_id',
                    'total_list_price',
                    'total',
                    'created_at'
                ];
            } else if (isClientCard) {
                colsToFetch = [
                    'client_name',
                    'client_state',
                    'client_region',
                    'client_id',
                    'project_id',
                    'user_full_name',
                    'user_state',
                    'user_region',
                    'user_channel',
                    'roleName',
                    'emp_code',
                    'total',
                    'created_at'
                ];
            } else {
                colsToFetch = [
                    'project_id',
                    'user_full_name',
                    'user_state',
                    'user_region',
                    'user_channel',
                    'client_name',
                    'client_state',
                    'client_region',
                    'total',
                    'created_at'
                ];
            }
        } else {
            colsToFetch = [
                'project_id',
                'draft_id',
                'user_full_name',
                'user_state',
                'user_region',
                'user_channel',
                'roleName',
                'emp_code',
                'client_name',
                'client_state',
                'client_region',
                'client_id',
                'sku_code',
                'product_desc',
                'qty',
                'total_list_price',
                'total',
                'created_at'
            ];
            
            const isDraftQuery = chart.xAxisColumn === 'draft_id' || chart.yAxisColumn === 'draft_id' || 
                                 chart.rowCol === 'draft_id' || chart.colCol === 'draft_id' || chart.valCol === 'draft_id';
            if (isDraftQuery) {
                colsToFetch = colsToFetch.filter(c => c !== 'project_id');
            } else {
                colsToFetch = colsToFetch.filter(c => c !== 'draft_id');
            }
        }

        const headingsMap = {
            'project_id': "Quotation ID",
            'draft_id': "Draft ID",
            'user_full_name': "User Name",
            'user_state': "User State",
            'user_region': "User Region",
            'user_channel': "User Channel",
            'roleName': "Role Name",
            'emp_code': "Employee ID",
            'client_name': "Client Name",
            'client_state': "Client State",
            'client_region': "Client Region",
            'client_id': "Client ID",
            'sku_code': "SKU Code",
            'product_desc': "Product Description",
            'qty': "Quantity",
            'total_list_price': "List Price",
            'total': "Offer Price",
            'created_at': "Created Date"
        };

        // Append active chart dimension columns dynamically if missing
        const extraCols = [];
        if (chart.type === 'heatmap') {
            if (chart.rowColumn && !colsToFetch.includes(chart.rowColumn)) extraCols.push(chart.rowColumn);
            if (chart.colColumn && !colsToFetch.includes(chart.colColumn)) extraCols.push(chart.colColumn);
            if (chart.valColumn && !colsToFetch.includes(chart.valColumn)) extraCols.push(chart.valColumn);
        } else {
            if (chart.xAxisColumn && !colsToFetch.includes(chart.xAxisColumn)) extraCols.push(chart.xAxisColumn);
            if (chart.yAxisColumn && !colsToFetch.includes(chart.yAxisColumn)) extraCols.push(chart.yAxisColumn);
        }

        const finalCols = [...colsToFetch, ...extraCols];
        const finalHeadings = finalCols.map(colId => headingsMap[colId] || formatLabel(colId));

        let baseGrain = null;
        if (chart.type === 'card') {
            if (isDraftCard) {
                baseGrain = 'draft';
            } else if (isQuotationCard) {
                baseGrain = 'quotation';
            } else if (isClientCard) {
                baseGrain = 'client';
            }
        }

        try {
            let page = 1;
            let hasMore = true;

            while (hasMore) {
                const response = await axios.post(
                    `${Apiurl}admin/dynamic-dashboard/data`,
                    {
                        columns: finalCols,
                        page: page,
                        limit: LIMIT,
                        sort_by: sortBy.sort_by,
                        sort_order: sortBy.order,
                        baseGrain: baseGrain,
                        ...currentFilters
                    },
                    {
                        signal: downloadAbortControllerRef.current.signal,
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );

                const pageData = response.data?.data?.data || [];
                const returnedTotal = response.data?.data?.total || 0;

                if (page === 1) {
                    setDownloadTotal(returnedTotal);
                }

                allDownloadedData = [...allDownloadedData, ...pageData];
                setExtractedCount(allDownloadedData.length);

                if (returnedTotal > 0) {
                    setProgress(Math.min(99, Math.round((allDownloadedData.length / returnedTotal) * 100)));
                }

                if (pageData.length < LIMIT || allDownloadedData.length >= returnedTotal) {
                    hasMore = false;
                } else {
                    page++;
                }
            }

            if (allDownloadedData.length === 0) {
                Swal.fire({
                    title: "No Data Available",
                    text: "There is no data to export based on current filters.",
                    icon: "info",
                    confirmButtonColor: "#38bdf8",
                });
                setIsDownloading(false);
                return;
            }

            const sheetData = [
                ["SL No.", ...finalHeadings],
                ...allDownloadedData.map((row, idx) => [
                    idx + 1,
                    ...finalCols.map(colId => {
                        if (['mrp', 'unit', 'total', 'total_list_price', 'qty'].includes(colId)) {
                            return row[colId] !== undefined && row[colId] !== null ? Number(row[colId]) : 0;
                        }
                        if (colId === 'created_at') {
                            return row.created_at ? new Date(row.created_at).toLocaleString("en-GB") : "N/A";
                        }
                        return row[colId] || "N/A";
                    })
                ])
            ];

            const ws = XLSX.utils.aoa_to_sheet(sheetData);
            const wscols = [{ wch: 8 }, ...finalHeadings.map(() => ({ wch: 20 }))];
            ws["!cols"] = wscols;

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Chart Detailed Data");

            const cleanTitle = (chart.title || "chart").replace(/[^a-z0-9]/gi, '_').toLowerCase();
            const fileName = `${cleanTitle}_data_${Date.now()}.xlsx`;
            XLSX.writeFile(wb, fileName);

            Swal.fire({
                title: "Export Successful!",
                html: `
                  <div style="text-align: left; padding: 10px;">
                    <p><strong>Chart:</strong> ${chart.title}</p>
                    <p><strong>Records exported:</strong> ${allDownloadedData.length.toLocaleString()}</p>
                    <p><strong>File name:</strong> ${fileName}</p>
                  </div>
                `,
                icon: "success",
                confirmButtonColor: "#10b981",
                confirmButtonText: "OK",
            });
        } catch (error) {
            if (!axios.isCancel(error)) {
                console.error("Error exporting chart data", error);
                Swal.fire({
                    title: "Export Failed",
                    text: error.message || "Failed to fetch data for export. Please try again.",
                    icon: "error",
                    confirmButtonColor: "#ef4444",
                });
            }
        } finally {
            setIsDownloading(false);
            setProgress(0);
            setDownloadTotal(0);
            setEstimatedTime("Calculating...");
            setStartTime(null);
        }
    };

    const handleClearFilters = () => {
        setSelectedUsers([]); setSelectedClients([]); setSelectedCategory([]); setSelectedSegment([]); setSelectedCollection([]);
        setSelectedBrands([]);
        setSelectedChannels([]);
        setSelectedFromDate(null); setSelectedToDate(null); setSearchTerm(""); setRoleId([]); setRegion([]); setState([]); setSku([]);
        
        const currentYrStr = String(new Date().getFullYear());
        setYear(FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr));
        
        const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
        const currentCalendarMonth = new Date().getMonth() + 1;
        const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
        setMonth(fullFYMonths.slice(0, currentMonthIndex + 1));

        localStorage.removeItem("default_view_filters");
    };

    const handleApplyGlobalFilter = (column, value) => {
        if (!value || value === 'Others') return;

        switch (column) {
            case 'client_region':
            case 'user_region': {
                const matchedRegion = (regionOpt || []).find(r => String(r.label).toLowerCase() === String(value).toLowerCase()) || { value, label: value };
                setRegion(prev => {
                    if (prev.some(r => String(r.value) === String(matchedRegion.value))) return prev;
                    return [...prev, matchedRegion];
                });
                break;
            }
            case 'client_state':
            case 'user_state': {
                const matchedState = (stateOpt || []).find(s => String(s.label).toLowerCase() === String(value).toLowerCase()) || { value, label: value };
                setState(prev => {
                    if (prev.some(s => String(s.value) === String(matchedState.value))) return prev;
                    return [...prev, matchedState];
                });
                break;
            }
            case 'category': {
                const matchedCat = (categoryOpt || []).find(c => String(c.label).toLowerCase() === String(value).toLowerCase() || String(c.value).toLowerCase() === String(value).toLowerCase()) || { value, label: value };
                setSelectedCategory(prev => {
                    if (prev.some(c => String(c.value) === String(matchedCat.value))) return prev;
                    return [...prev, matchedCat];
                });
                break;
            }
            case 'brand': {
                const matchedBrand = { value: value, label: value };
                setSelectedBrands(prev => {
                    if (prev.some(b => String(b.value) === String(matchedBrand.value))) return prev;
                    return [...prev, matchedBrand];
                });
                break;
            }
            case 'priceSegment': {
                const matchedSeg = (segmentOpt || []).find(s => String(s.label).toLowerCase() === String(value).toLowerCase() || String(s.value).toLowerCase() === String(value).toLowerCase()) || { value, label: value };
                setSelectedSegment(prev => {
                    if (prev.some(s => String(s.value) === String(matchedSeg.value))) return prev;
                    return [...prev, matchedSeg];
                });
                break;
            }
            case 'collection': {
                const matchedColl = (collectionOpt || []).find(c => String(c.label).toLowerCase() === String(value).toLowerCase() || String(c.value).toLowerCase() === String(value).toLowerCase()) || { value, label: value };
                setSelectedCollection(prev => {
                    if (prev.some(c => String(c.value) === String(matchedColl.value))) return prev;
                    return [...prev, matchedColl];
                });
                break;
            }
            case 'user_full_name':
            case 'user_id': {
                const matchedUser = (userOpt || []).find(u => String(u.label).toLowerCase() === String(value).toLowerCase()) || { value, label: value };
                setSelectedUsers(prev => {
                    if (prev.some(u => String(u.value) === String(matchedUser.value))) return prev;
                    return [...prev, matchedUser];
                });
                break;
            }
            case 'user_channel': {
                const matchedChannel = (channelOpt || []).find(c => String(c.label).toLowerCase() === String(value).toLowerCase()) || { value, label: value };
                setSelectedChannels(prev => {
                    if (prev.some(c => String(c.value) === String(matchedChannel.value))) return prev;
                    return [...prev, matchedChannel];
                });
                break;
            }
            case 'client_name':
            case 'client_id': {
                const matchedClient = (clientOpt || []).find(c => String(c.label).toLowerCase() === String(value).toLowerCase()) || { value, label: value };
                setSelectedClients(prev => {
                    if (prev.some(c => String(c.value) === String(matchedClient.value))) return prev;
                    return [...prev, matchedClient];
                });
                break;
            }
            case 'sku_code': {
                const matchedSku = (skuOpt || []).find(s => String(s.label).toLowerCase() === String(value).toLowerCase()) || { value, label: value };
                setSku(prev => {
                    if (prev.some(s => String(s.value) === String(matchedSku.value))) return prev;
                    return [...prev, matchedSku];
                });
                break;
            }
            case 'roleName': {
                const matchedRole = (roleOpt || []).find(r => String(r.label).toLowerCase() === String(value).toLowerCase()) || { value, label: value };
                setRoleId(prev => {
                    if (prev.some(r => String(r.value) === String(matchedRole.value))) return prev;
                    return [...prev, matchedRole];
                });
                break;
            }
            case 'created_at_year': {
                const matchedYear = FY_YEAR_OPTIONS.find(y => String(y.value) === String(value)) || { value: value, label: value };
                setYear(prev => {
                    if (prev.some(y => String(y.value) === String(matchedYear.value))) return prev;
                    return [...prev, matchedYear];
                });
                break;
            }
            case 'created_at_month': {
                let monthNum = null;
                if (value.includes('-')) {
                    monthNum = parseInt(value.split('-')[1], 10);
                } else if (!isNaN(value)) {
                    monthNum = parseInt(value, 10);
                }
                if (monthNum && monthNum >= 1 && monthNum <= 12) {
                    setMonth(prev => {
                        if (prev.includes(monthNum)) return prev;
                        return [...prev, monthNum];
                    });
                }
                break;
            }
            case 'created_at':
            case 'created_at_day': {
                setSelectedFromDate(value);
                setSelectedToDate(value);
                break;
            }
            default:
                break;
        }
    };

    const handleClearGlobalFilter = (column, value) => {
        if (!value || value === 'Others') return;

        switch (column) {
            case 'client_region':
            case 'user_region':
                setRegion(prev => prev.filter(r => String(r.label).toLowerCase() !== String(value).toLowerCase() && String(r.value).toLowerCase() !== String(value).toLowerCase()));
                break;
            case 'client_state':
            case 'user_state':
                setState(prev => prev.filter(s => String(s.label).toLowerCase() !== String(value).toLowerCase() && String(s.value).toLowerCase() !== String(value).toLowerCase()));
                break;
            case 'category':
                setSelectedCategory(prev => prev.filter(c => String(c.label).toLowerCase() !== String(value).toLowerCase() && String(c.value).toLowerCase() !== String(value).toLowerCase()));
                break;
            case 'brand':
                setSelectedBrands(prev => prev.filter(b => String(b.value).toLowerCase() !== String(value).toLowerCase()));
                break;
            case 'priceSegment':
                setSelectedSegment(prev => prev.filter(s => String(s.label).toLowerCase() !== String(value).toLowerCase() && String(s.value).toLowerCase() !== String(value).toLowerCase()));
                break;
            case 'collection':
                setSelectedCollection(prev => prev.filter(c => String(c.label).toLowerCase() !== String(value).toLowerCase() && String(c.value).toLowerCase() !== String(value).toLowerCase()));
                break;
            case 'user_full_name':
            case 'user_id':
                setSelectedUsers(prev => prev.filter(u => String(u.label).toLowerCase() !== String(value).toLowerCase() && String(u.value).toLowerCase() !== String(value).toLowerCase()));
                break;
            case 'user_channel':
                setSelectedChannels(prev => prev.filter(c => String(c.label).toLowerCase() !== String(value).toLowerCase() && String(c.value).toLowerCase() !== String(value).toLowerCase()));
                break;
            case 'client_name':
            case 'client_id':
                setSelectedClients(prev => prev.filter(c => String(c.label).toLowerCase() !== String(value).toLowerCase() && String(c.value).toLowerCase() !== String(value).toLowerCase()));
                break;
            case 'sku_code':
                setSku(prev => prev.filter(s => String(s.label).toLowerCase() !== String(value).toLowerCase() && String(s.value).toLowerCase() !== String(value).toLowerCase()));
                break;
            case 'roleName':
                setRoleId(prev => prev.filter(r => String(r.label).toLowerCase() !== String(value).toLowerCase() && String(r.value).toLowerCase() !== String(value).toLowerCase()));
                break;
            case 'created_at_year':
                setYear(prev => prev.filter(y => String(y.value) !== String(value)));
                break;
            case 'created_at_month': {
                let monthNum = null;
                if (value.includes('-')) {
                    monthNum = parseInt(value.split('-')[1], 10);
                } else if (!isNaN(value)) {
                    monthNum = parseInt(value, 10);
                }
                if (monthNum) {
                    setMonth(prev => prev.filter(m => m !== monthNum));
                }
                break;
            }
            case 'created_at':
            case 'created_at_day':
                setSelectedFromDate(null);
                setSelectedToDate(null);
                break;
            default:
                break;
        }
    };

    const filterConfigs = [
        { id: "client", label: "Client", options: clientOpt || [], selected: selectedClients },
        { id: "user", label: "User", options: userOpt || [], selected: selectedUsers },
        { id: "sku", label: "SKU", options: skuOpt || [], selected: sku },
        { id: "category", label: "Category", options: categoryOpt || [], selected: selectedCategory },
        { id: "segment", label: "Segment", options: segmentOpt || [], selected: selectedSegment },
        { id: "collection", label: "Collection", options: collectionOpt || [], selected: selectedCollection },
        { id: "role", label: "Role", options: roleOpt || [], selected: roleId },
        { id: "channel", label: "Channel", options: channelOpt || [], selected: selectedChannels },
        { id: "region", label: "Region", options: regionOpt || [], selected: region },
        { id: "state", label: "State", options: stateOpt || [], selected: state },
        { id: "year", label: "Year (FY)", options: FY_YEAR_OPTIONS, selected: year },
    ];

    const handleFilterChange = (filterId, nextSelected) => {
        switch (filterId) {
            case "client": setSelectedClients(nextSelected); break;
            case "user": setSelectedUsers(nextSelected); break;
            case "sku": setSku(nextSelected); break;
            case "category": setSelectedCategory(nextSelected); break;
            case "segment": setSelectedSegment(nextSelected); break;
            case "collection": setSelectedCollection(nextSelected); break;
            case "role": setRoleId(nextSelected); break;
            case "channel": setSelectedChannels(nextSelected); break;
            case "region": setRegion(nextSelected); break;
            case "state": setState(nextSelected); break;
            case "year": 
                setYear(nextSelected); 
                if (nextSelected.length === 0) setMonth([]);
                break;
            default: break;
        }
    };

    const handleSort = (columnKey) => {
        setSortBy(prev => ({
            sort_by: columnKey,
            order: prev.sort_by === columnKey && prev.order === "asc" ? "desc" : "asc"
        }));
    };

    const renderCell = (row, key) => {
        if (row[key] === undefined) {
            return (
                <span className="inline-block w-16 h-3 bg-gray-200 animate-pulse rounded" />
            );
        }
        if (key === 'sku_code') {
            return (
                <ClickableTableLink
                    path="/admin/product"
                    displayValue={row.sku_code}
                    searchValue={row.sku_code}
                />
            );
        }
        if (key === 'project_id') {
            return (
                <ClickableTableLink
                    path="/admin/projects"
                    displayValue={row.project_id}
                    searchValue={row.project_id}
                />
            );
        }
        if (key === 'client_name') {
            return (
                <ClickableTableLink
                    path="/admin/clientlist"
                    displayValue={row.client_name}
                    searchValue={row.client_name}
                />
            );
        }
        if (key === 'user_full_name') {
            return (
                <ClickableTableLink
                    path="/admin/userlist"
                    displayValue={row.user_full_name}
                    searchValue={row.user_full_name}
                />
            );
        }
        if (key === 'roleName') {
            const match = roleOpt.find(r => r.label === row.roleName);
            return (
                <ClickableTableLink
                    path="/admin/userlist"
                    displayValue={row.roleName}
                    searchValue=""
                    role={match ? match.value : undefined}
                />
            );
        }
        if (['mrp', 'unit', 'total', 'total_list_price'].includes(key)) {
            return row[key] !== undefined && row[key] !== null 
                ? Number(row[key]).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) 
                : "N/A";
        }
        if (key === 'created_at') {
            return row.created_at ? new Date(row.created_at).toLocaleString("en-GB") : "N/A";
        }
        return row[key] || "N/A";
    };



    const renderUnifiedLayout = () => {
        return (
            <Box sx={{ mt: 2, mb: 2 }}>
                <Grid container spacing={3}>
                    {layout.map((itemId, index) => {
                        const chart = charts.find(c => c.id === itemId);
                        if (!chart) return null;

                        const isHeatmap = chart.type === 'heatmap';
                        const gridWidths = chart.type === 'card'
                            ? { xs: 12, sm: 6, md: 4, lg: 3 }
                            : (chart.size === 'full'
                                ? { xs: 12, md: 12 }
                                : (charts.length === 1 ? { xs: 12, md: 8, lg: 6.5 } : { xs: 12, md: 6 }));
                        const chartHeight = chart.size === 'full' ? 420 : (charts.length === 1 ? 420 : 340);

                        let displayTitle = chart.title;
                        if (!displayTitle || displayTitle.trim() === "" || displayTitle.toLowerCase() === "custom chart") {
                            if (isHeatmap) {
                                const valLabel = formatLabel(chart.valColumn);
                                const rowLabel = formatLabel(chart.rowColumn);
                                const colLabel = formatLabel(chart.colColumn);
                                const funcLabel = formatAgg(chart.aggregateFunction);
                                displayTitle = `${valLabel} by ${rowLabel} & ${colLabel} (${funcLabel})`;
                            } else if (chart.type === 'card') {
                                const valLabel = formatLabel(chart.yAxisColumn);
                                const funcLabel = formatAgg(chart.aggregateFunction);
                                displayTitle = `${valLabel} (${funcLabel})`;
                            } else {
                                const yLabel = formatLabel(chart.yAxisColumn);
                                const xLabel = formatLabel(chart.xAxisColumn);
                                const funcLabel = formatAgg(chart.aggregateFunction);
                                displayTitle = `${yLabel} by ${xLabel} (${funcLabel})`;
                            }
                        }

                        if (chart.type === 'card') {
                            return (
                                <Grid 
                                    item 
                                    {...gridWidths}
                                    key={chart.id}
                                    draggable
                                    onDragStart={(e) => handleLayoutDragStart(e, index)}
                                    onDragOver={(e) => handleLayoutDragOver(e, index)}
                                    onDragEnd={handleLayoutDragEnd}
                                    onMouseUp={handleLayoutDragEnd}
                                    sx={{
                                        opacity: draggingLayoutIndex === index ? 0.5 : 1,
                                        transition: 'opacity 0.15s ease',
                                        cursor: 'grab',
                                        alignSelf: 'flex-start',
                                        '&:active': { cursor: 'grabbing' }
                                    }}
                                >
                                    <DashboardCardCard
                                        chart={chart}
                                        currentFilters={currentFilters}
                                        onEdit={() => handleOpenChartModal(chart)}
                                        onDelete={() => handleDeleteChart(chart.id)}
                                        onDownload={() => handleDownloadChartData(chart)}
                                    />
                                </Grid>
                            );
                        }

                        if (chart.type === 'table') {
                            return (
                                <Grid 
                                    item 
                                    {...gridWidths}
                                    key={chart.id}
                                    draggable
                                    onDragStart={(e) => handleLayoutDragStart(e, index)}
                                    onDragOver={(e) => handleLayoutDragOver(e, index)}
                                    onDragEnd={handleLayoutDragEnd}
                                    onMouseUp={handleLayoutDragEnd}
                                    sx={{
                                        opacity: draggingLayoutIndex === index ? 0.5 : 1,
                                        transition: 'opacity 0.15s ease',
                                        cursor: 'grab',
                                        '&:active': { cursor: 'grabbing' }
                                    }}
                                >
                                    <DashboardTableCard
                                        chart={chart}
                                        currentFilters={currentFilters}
                                        onEdit={() => handleOpenChartModal(chart)}
                                        onDelete={() => handleDeleteChart(chart.id)}
                                        roleOpt={roleOpt}
                                        AVAILABLE_COLUMNS={AVAILABLE_COLUMNS}
                                        DEFAULT_COLUMNS={DEFAULT_COLUMNS}
                                        onColumnsChange={(newCols) => handleUpdateChartColumns(chart.id, newCols)}
                                    />
                                </Grid>
                            );
                        }

                        return (
                            <Grid 
                                item 
                                {...gridWidths}
                                key={chart.id}
                                draggable
                                onDragStart={(e) => handleLayoutDragStart(e, index)}
                                onDragOver={(e) => handleLayoutDragOver(e, index)}
                                onDragEnd={handleLayoutDragEnd}
                                onMouseUp={handleLayoutDragEnd}
                                sx={{
                                    opacity: draggingLayoutIndex === index ? 0.5 : 1,
                                    transition: 'opacity 0.15s ease',
                                    cursor: 'grab',
                                    '&:active': { cursor: 'grabbing' }
                                }}
                            >
                                <DashboardChartCard
                                    chart={chart}
                                    currentFilters={currentFilters}
                                    onEdit={() => handleOpenChartModal(chart)}
                                    onDelete={() => handleDeleteChart(chart.id)}
                                    onDownload={() => handleDownloadChartData(chart)}
                                    isDownloading={isDownloading}
                                    chartHeight={chartHeight}
                                    onGlobalFilterApply={handleApplyGlobalFilter}
                                    onGlobalFilterClear={handleClearGlobalFilter}
                                />
                            </Grid>
                        );
                    })}
                </Grid>
            </Box>
        );
    };

    const handleViewChange = (id) => {
        if (id === "") {
            setSelectedDashboardId("");
            localStorage.setItem("active_dashboard_id", "");
            // Reset selected columns to the saved default layout configuration
            try {
                const stored = localStorage.getItem("default_view_columns");
                setSelectedColumns(stored ? JSON.parse(stored) : DEFAULT_COLUMNS);
            } catch (err) {
                setSelectedColumns(DEFAULT_COLUMNS);
            }
            // Reset charts to default config
            try {
                const stored = localStorage.getItem("default_view_charts");
                setCharts(stored ? JSON.parse(stored) : []);
            } catch (err) {
                setCharts([]);
            }
            // Reset table position to default
            const storedPos = localStorage.getItem("default_view_table_position") || 'bottom';
            setTablePosition(storedPos);

            // Reset unified layout
            try {
                const stored = localStorage.getItem("default_view_charts");
                const storedLayout = localStorage.getItem("default_view_layout_order");
                if (storedLayout) {
                    const parsedLayout = JSON.parse(storedLayout);
                    if (Array.isArray(parsedLayout) && parsedLayout.length > 0) {
                        setLayout(parsedLayout.filter(id => id !== 'table'));
                    } else {
                        const defCharts = stored ? JSON.parse(stored) : [];
                        setLayout(defCharts.map(c => c.id));
                    }
                } else {
                    const defCharts = stored ? JSON.parse(stored) : [];
                    setLayout(defCharts.map(c => c.id));
                }
            } catch (err) {
                setLayout([]);
            }
            
            // Reset filters to default configuration
            setSearchTerm(getStoredFilter("search", ""));
            setSelectedUsers(getStoredFilter("selectedUsers", []));
            setSelectedClients(getStoredFilter("selectedClients", []));
            setSelectedCollection(getStoredFilter("selectedCollection", []));
            setSelectedSegment(getStoredFilter("selectedSegment", []));
            setSelectedCategory(getStoredFilter("selectedCategory", []));
            setSelectedChannels(getStoredFilter("selectedChannels", []));
            setSelectedBrands([]);
            setRoleId(getStoredFilter("roleId", []));
            setRegion(getStoredFilter("region", []));
            setState(getStoredFilter("state", []));
            setSelectedFromDate(null);
            setSelectedToDate(null);
            setSku(getStoredFilter("sku", []));
            
            const storedMonths = getStoredFilter("month", null);
            if (storedMonths) {
                setMonth(storedMonths.split(",").map(Number));
            } else {
                const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
                const currentCalendarMonth = new Date().getMonth() + 1;
                const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
                setMonth(fullFYMonths.slice(0, currentMonthIndex + 1));
            }
            
            const storedYear = getStoredFilter("year", null);
            if (storedYear) {
                setYear(FY_YEAR_OPTIONS.filter(y => y.value === storedYear));
            } else {
                const currentYrStr = String(new Date().getFullYear());
                setYear(FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr));
            }
        } else {
            const d = savedDashboards.find(item => String(item.id) === String(id));
            if (d) handleLoadDashboard(d);
        }
    };
const isCustomViewActive = selectedDashboardId !== "" && selectedDashboardId !== "new_blank";

    return (
        <MainLayout title={'Dynamic Dashboard'} disableScroll={true}>
            {/* Filter Bar */}
            <Box sx={{ 
                flexShrink: 0,
                bgcolor: 'rgba(248, 250, 252, 0.95)', 
                backdropFilter: 'blur(8px)',
                pt: 1.5, 
                pb: 1.5,
                ml: -2,
                mr: -2,
                pl: 2,
                pr: 2,
                borderBottom: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                transition: 'all 0.25s ease',
                position: 'relative',
                zIndex: 110
            }}>
                <FilterBar2
                    filters={filterConfigs}
                    onFilterChange={handleFilterChange}
                    selectedMonth={month}
                    setSelectedMonth={setMonth}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    searchWidth="270px"
                    onClearFilters={handleClearFilters}
                    viewControls={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box ref={viewSelectorRef} sx={{ position: 'relative', display: 'inline-block', width: '170px' }}>
                                <button
                                    type="button"
                                    onClick={() => setIsViewSelectorOpen(!isViewSelectorOpen)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '6px',
                                        padding: '4px 10px',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(0,0,0,0.15)',
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        outline: 'none',
                                        backgroundColor: 'white',
                                        color: 'rgba(0,0,0,0.7)',
                                        cursor: 'pointer',
                                        height: '30px',
                                        width: '100%',
                                        boxSizing: 'border-box',
                                        transition: 'all 0.15s ease'
                                    }}
                                >
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {selectedDashboardId === "" 
                                            ? defaultViewName 
                                            : (selectedDashboardId === "new_blank" 
                                                ? "New Layout (Blank Table)" 
                                                : (savedDashboards.find(d => String(d.id) === String(selectedDashboardId))?.name || "Custom View"))}
                                    </span>
                                    <KeyboardArrowDownIcon
                                        sx={{
                                            fontSize: 13,
                                            opacity: 0.6,
                                            transform: isViewSelectorOpen ? "rotate(180deg)" : "none",
                                            transition: "transform 0.15s ease",
                                        }}
                                    />
                                </button>

                                {isViewSelectorOpen && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 'calc(100% + 4px)',
                                            left: 0,
                                            zIndex: 1000,
                                            minWidth: '220px',
                                            bgcolor: 'white',
                                            border: '1px solid rgba(0,0,0,0.12)',
                                            borderRadius: '8px',
                                            boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                                            p: 0.8,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '2px',
                                            maxHeight: '260px',
                                            overflowY: 'auto'
                                        }}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => {
                                                handleViewChange("");
                                                setIsViewSelectorOpen(false);
                                            }}
                                            style={{
                                                display: 'block',
                                                width: '100%',
                                                textAlign: 'left',
                                                padding: '6px 10px',
                                                border: 'none',
                                                borderRadius: '6px',
                                                fontSize: '11px',
                                                fontWeight: selectedDashboardId === "" ? '700' : '500',
                                                color: selectedDashboardId === "" ? '#4f46e5' : '#334155',
                                                backgroundColor: selectedDashboardId === "" ? 'rgba(79, 70, 229, 0.05)' : 'transparent',
                                                cursor: 'pointer',
                                                transition: 'all 0.15s ease',
                                            }}
                                            onMouseEnter={(e) => {
                                                if (selectedDashboardId !== "") e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.03)';
                                            }}
                                            onMouseLeave={(e) => {
                                                if (selectedDashboardId !== "") e.currentTarget.style.backgroundColor = 'transparent';
                                            }}
                                        >
                                            {defaultViewName} (Default)
                                        </button>

                                        {selectedDashboardId === "new_blank" && (
                                            <button
                                                type="button"
                                                disabled
                                                style={{
                                                    display: 'block',
                                                    width: '100%',
                                                    textAlign: 'left',
                                                    padding: '6px 10px',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    fontSize: '11px',
                                                    fontWeight: '700',
                                                    color: '#4f46e5',
                                                    backgroundColor: 'rgba(79, 70, 229, 0.05)',
                                                    cursor: 'default',
                                                }}
                                            >
                                                New Layout (Blank Table)
                                            </button>
                                        )}

                                        {savedDashboards.map((d) => (
                                            <button
                                                key={d.id}
                                                type="button"
                                                onClick={() => {
                                                    handleViewChange(d.id);
                                                    setIsViewSelectorOpen(false);
                                                }}
                                                style={{
                                                    display: 'block',
                                                    width: '100%',
                                                    textAlign: 'left',
                                                    padding: '6px 10px',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    fontSize: '11px',
                                                    fontWeight: String(selectedDashboardId) === String(d.id) ? '700' : '500',
                                                    color: String(selectedDashboardId) === String(d.id) ? '#4f46e5' : '#334155',
                                                    backgroundColor: String(selectedDashboardId) === String(d.id) ? 'rgba(79, 70, 229, 0.05)' : 'transparent',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.15s ease',
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (String(selectedDashboardId) !== String(d.id)) e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.03)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (String(selectedDashboardId) !== String(d.id)) e.currentTarget.style.backgroundColor = 'transparent';
                                                }}
                                            >
                                                {d.name}
                                            </button>
                                        ))}
                                    </Box>
                                )}
                            </Box>



                            {/* Edit / Rename view button */}
                            <Tooltip title="Rename Active View" arrow>
                                <IconButton 
                                    size="small" 
                                    onClick={handleRenameActiveView}
                                    sx={{ color: "rgba(0,0,0,0.5)", "&:hover": { color: "#4f46e5" }, height: '30px', width: '30px' }}
                                >
                                    <EditIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>

                            {/* Create view button */}
                            <Tooltip title="Create Custom View" arrow>
                                <IconButton 
                                    size="small" 
                                    onClick={promptNewViewSave}
                                    sx={{ color: "rgba(0,0,0,0.5)", "&:hover": { color: "#4f46e5" }, height: '30px', width: '30px' }}
                                >
                                    <AddIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>

                            {/* Save changes to active view */}
                            <Tooltip title="Save Changes" arrow>
                                <IconButton 
                                    size="small" 
                                    onClick={() => {
                                        if (selectedDashboardId === "" || selectedDashboardId === "new_blank") {
                                            handleSaveDefaultConfig();
                                        } else {
                                            const currentView = savedDashboards.find(d => String(d.id) === String(selectedDashboardId));
                                            if (currentView) {
                                                handleUpdateCustomView(selectedDashboardId, currentView.name);
                                            }
                                        }
                                    }}
                                    sx={{ color: "#10b981", height: '30px', width: '30px' }}
                                >
                                    <SaveIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>

                            {/* Reset/clear slate */}
                            <Tooltip title="Reset View" arrow>
                                <IconButton 
                                    size="small" 
                                    onClick={() => {
                                        setSelectedColumns([]);
                                        handleClearFilters();
                                    }}
                                    sx={{ color: "#f59e0b", height: '30px', width: '30px' }}
                                >
                                    <RestartAltIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>

                            {/* Delete custom view */}
                            {isCustomViewActive && (
                                <Tooltip title="Delete View" arrow>
                                    <IconButton 
                                        size="small" 
                                        onClick={() => handleDeleteDashboardById(selectedDashboardId)}
                                        sx={{ color: "rgba(0,0,0,0.4)", "&:hover": { color: "#ef4444" }, height: '30px', width: '30px' }}
                                    >
                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            )}

                            <span style={{ width: '1px', height: '16px', backgroundColor: '#e2e8f0', margin: '0 4px' }} />

                            {/* Add Chart Button */}
                            <Tooltip title="Add Dynamic Chart" arrow>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleOpenChartModal(null)}
                                    sx={{ 
                                        textTransform: "none", 
                                        borderColor: "rgba(0,0,0,0.15)",
                                        color: "#4f46e5",
                                        bgcolor: "white",
                                        py: 0,
                                        px: 1,
                                        borderRadius: 1.5,
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        height: '30px',
                                        boxSizing: 'border-box',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        "&:hover": { borderColor: "#4f46e5", bgcolor: "rgba(79, 70, 229, 0.05)" }
                                    }}
                                >
                                    <AddIcon sx={{ fontSize: 14 }} />
                                    <span>Chart</span>
                                </Button>
                            </Tooltip>
                        </Box>
                    }
                />
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', mt: 2, mx: -2, px: 2, pb: 4, minHeight: 0 }}>
                {renderUnifiedLayout()}
            </Box>
            {/* Progressive Export Overlay */}
            <Portal>
                <Backdrop
                    sx={{
                        color: "#fff",
                        zIndex: (theme) => theme.zIndex.drawer + 9999,
                        backdropFilter: "blur(4px)",
                        backgroundColor: "rgba(0, 0, 0, 0.7)",
                    }}
                    open={isDownloading}
                >
                    <Fade in={isDownloading}>
                        <Card
                            sx={{
                                width: 420,
                                p: 0,
                                borderRadius: 3,
                                overflow: "hidden",
                                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
                                border: "1px solid rgba(255, 255, 255, 0.1)",
                            }}
                        >
                            {/* Header */}
                            <Box
                                sx={{
                                    bgcolor: "#04121f",
                                    p: 3,
                                    color: "white",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                }}
                            >
                                <DownloadingIcon sx={{ fontSize: 28 }} />
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Exporting Report
                                    </Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                        Please wait while we prepare your file
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Content */}
                            <Box sx={{ p: 3, bgcolor: "white" }}>
                                {/* Progress Bar */}
                                <Box sx={{ mb: 3 }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            mb: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontWeight: 500 }}
                                        >
                                            Export Progress
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            color="primary"
                                            sx={{ fontWeight: 700 }}
                                        >
                                            {progress}%
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={progress}
                                        sx={{
                                            height: 10,
                                            borderRadius: 5,
                                            bgcolor: "grey.100",
                                            "& .MuiLinearProgress-bar": {
                                                borderRadius: 5,
                                                background: "linear-gradient(90deg, #38bdf8, #0284c7)",
                                            },
                                        }}
                                    />
                                </Box>

                                {/* Stats */}
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gap: 2,
                                        mb: 3,
                                    }}
                                >
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            borderColor: "grey.200",
                                            bgcolor: "white"
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: "block", mb: 0.5 }}
                                        >
                                            Records Processed
                                        </Typography>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <FileDownloadDoneIcon
                                                color="success"
                                                sx={{ fontSize: 20 }}
                                            />
                                            <Typography variant="h6" sx={{ fontWeight: 700, color: "black" }}>
                                                {extractedCount.toLocaleString()}
                                            </Typography>
                                        </Box>
                                        <Typography variant="caption" color="text.secondary">
                                            of {totalRecords.toLocaleString()} total
                                        </Typography>
                                    </Paper>

                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            borderColor: "grey.200",
                                            bgcolor: "white"
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: "block", mb: 0.5 }}
                                        >
                                            Time Remaining
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            sx={{ fontWeight: 700, color: "#ed6c02" }}
                                        >
                                            {estimatedTime}
                                        </Typography>
                                    </Paper>
                                </Box>

                                {/* Cancel Button */}
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<CloseIcon />}
                                    onClick={handleCancelDownload}
                                    fullWidth
                                    sx={{
                                        textTransform: "none",
                                        py: 1.5,
                                        borderRadius: 2,
                                        fontWeight: 600,
                                        borderWidth: 2,
                                        "&:hover": {
                                            borderWidth: 2,
                                            bgcolor: "error.light",
                                            color: "error.dark",
                                        },
                                    }}
                                >
                                    Cancel Export
                                </Button>

                                {/* Important Note */}
                                <Typography
                                    variant="caption"
                                    color="error.main"
                                    sx={{
                                        display: "block",
                                        textAlign: "center",
                                        mt: 2,
                                        fontWeight: 500,
                                        bgcolor: "#fef2f2",
                                        p: 1,
                                        borderRadius: 1,
                                    }}
                                >
                                    ⚠️ Do not close this window while export is in progress
                                </Typography>
                            </Box>
                        </Card>
                    </Fade>
                </Backdrop>
            </Portal>
            {/* Chart Builder Modal */}
            {isChartModalOpen && (
                <Portal>
                    <Backdrop
                        sx={{
                            color: "#fff",
                            zIndex: (theme) => theme.zIndex.drawer + 9999,
                            backgroundColor: "rgba(0, 0, 0, 0.6)",
                        }}
                        transitionDuration={0}
                        open={isChartModalOpen}
                    >
                        <Fade in={isChartModalOpen} timeout={0}>
                            <Card
                                sx={{
                                    width: 460,
                                    maxHeight: "90vh",
                                    display: "flex",
                                    flexDirection: "column",
                                    p: 0,
                                    borderRadius: 3,
                                    overflow: "hidden",
                                    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
                                    border: "1px solid rgba(255, 255, 255, 0.1)",
                                }}
                            >
                                {/* Header */}
                                <Box sx={{ bgcolor: "#04121f", p: 2.5, color: "white", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {editingChart ? "Edit Dynamic Chart" : "Configure Dynamic Chart"}
                                        </Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                            Select dimensions and metrics to plot
                                        </Typography>
                                    </Box>
                                    <IconButton size="small" onClick={() => setIsChartModalOpen(false)} sx={{ color: "white" }}>
                                        <CloseIcon />
                                    </IconButton>
                                </Box>
                                {/* Tabs */}
                                <Box sx={{ display: 'flex', borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                                    {[
                                        { id: 'general', label: 'General' },
                                        { id: 'filters', label: 'Local Filters' },
                                        { id: 'formatting', label: 'Formatting' }
                                    ].map(tab => {
                                        const isActive = modalTab === tab.id;
                                        return (
                                            <Button
                                                key={tab.id}
                                                onClick={() => setModalTab(tab.id)}
                                                sx={{
                                                    flex: 1,
                                                    py: 1.5,
                                                    fontSize: '12px',
                                                    fontWeight: isActive ? 700 : 500,
                                                    color: isActive ? '#4f46e5' : '#64748b',
                                                    borderBottom: isActive ? '2px solid #4f46e5' : 'none',
                                                    borderRadius: 0,
                                                    textTransform: 'none',
                                                    '&:hover': {
                                                        bgcolor: 'rgba(79, 70, 229, 0.05)'
                                                    }
                                                }}
                                            >
                                                {tab.label}
                                            </Button>
                                        );
                                    })}
                                </Box>

                                {/* Form */}
                                <Box sx={{ p: 3, bgcolor: "white", display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', flexGrow: 1 }}>
                                    {modalTab === 'general' && (
                                        <>
                                            {/* Chart Title */}
                                            <Box>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                                    Chart Title
                                                </Typography>
                                                <input
                                                    type="text"
                                                    value={chartTitle}
                                                    onChange={(e) => setChartTitle(e.target.value)}
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px 12px',
                                                        borderRadius: '6px',
                                                        border: '1px solid rgba(0,0,0,0.2)',
                                                        fontSize: '13px',
                                                        outline: 'none',
                                                        boxSizing: 'border-box'
                                                    }}
                                                />
                                            </Box>

                                            {/* Chart Type */}
                                            <Box>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                                    Visualisation Type
                                                </Typography>
                                                <Select
                                                    options={chartTypeOptions}
                                                    value={chartTypeOptions.find(o => o.value === chartType)}
                                                    onChange={(opt) => {
                                                        const val = opt ? opt.value : 'bar';
                                                        setChartType(val);
                                                    }}
                                                    styles={reactSelectStyles}
                                                    isSearchable={false}
                                                    menuPortalTarget={document.body}
                                                />
                                            </Box>

                                            {/* Layout Width Selection */}
                                            {chartType !== 'card' && (
                                                <Box>
                                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                                        Layout Width
                                                    </Typography>
                                                    <Select
                                                        options={chartWidthOptions}
                                                        value={chartWidthOptions.find(o => o.value === chartWidth)}
                                                        onChange={(opt) => setChartWidth(opt ? opt.value : 'half')}
                                                        styles={reactSelectStyles}
                                                        isSearchable={false}
                                                        menuPortalTarget={document.body}
                                                    />
                                                </Box>
                                            )}

                                            {chartType === 'heatmap' ? (
                                                <>
                                                    {/* Row Dimension */}
                                                    <Box>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                                            Row Dimension (e.g. Roles/User Names)
                                                        </Typography>
                                                        <Select
                                                            options={rowColOptions}
                                                            value={rowColOptions.find(o => o.value === chartRowCol)}
                                                            onChange={(opt) => setChartRowCol(opt ? opt.value : '')}
                                                            styles={reactSelectStyles}
                                                            isSearchable={true}
                                                            menuPortalTarget={document.body}
                                                        />
                                                    </Box>

                                                    {/* Column Dimension */}
                                                    <Box>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                                            Column Dimension (e.g. States/Months)
                                                        </Typography>
                                                        <Select
                                                            options={colColOptions}
                                                            value={colColOptions.find(o => o.value === chartColCol)}
                                                            onChange={(opt) => setChartColCol(opt ? opt.value : '')}
                                                            styles={reactSelectStyles}
                                                            isSearchable={true}
                                                            menuPortalTarget={document.body}
                                                        />
                                                    </Box>

                                                    {/* Grid Cell Value (Metric) */}
                                                    <Box>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                                            Grid Cell Value (Metric)
                                                        </Typography>
                                                        <Select
                                                            options={valColOptions}
                                                            value={valColOptions.find(o => o.value === chartValCol)}
                                                            onChange={(opt) => setChartValCol(opt ? opt.value : '')}
                                                            styles={reactSelectStyles}
                                                            isSearchable={true}
                                                            menuPortalTarget={document.body}
                                                        />
                                                    </Box>

                                                    {/* Aggregation Function */}
                                                    <Box>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                                            Aggregation Function
                                                        </Typography>
                                                        <Select
                                                            options={aggregateOptions}
                                                            value={aggregateOptions.find(o => o.value === chartFunc)}
                                                            onChange={(opt) => setChartFunc(opt ? opt.value : 'sum')}
                                                            styles={reactSelectStyles}
                                                            isSearchable={false}
                                                            menuPortalTarget={document.body}
                                                        />
                                                    </Box>
                                                </>
                                            ) : chartType === 'table' ? (
                                                <Box>
                                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}>
                                                        Select Table Columns
                                                    </Typography>
                                                    <Box sx={{ 
                                                        maxHeight: 180, 
                                                        overflowY: 'auto', 
                                                        border: '1px solid rgba(0,0,0,0.15)', 
                                                        borderRadius: '8px', 
                                                        p: 1.5,
                                                        display: 'grid',
                                                        gridTemplateColumns: 'repeat(2, 1fr)',
                                                        gap: 1.5
                                                    }}>
                                                        {AVAILABLE_COLUMNS.map(col => {
                                                            const isChecked = chartTableColumns.includes(col.id);
                                                            return (
                                                                <label 
                                                                    key={col.id} 
                                                                    style={{ 
                                                                        display: 'flex', 
                                                                        alignItems: 'center', 
                                                                        gap: '8px',
                                                                        fontSize: '11px',
                                                                        fontWeight: '600',
                                                                        color: '#334155',
                                                                        cursor: 'pointer',
                                                                        userSelect: 'none'
                                                                    }}
                                                                >
                                                                    <input 
                                                                        type="checkbox"
                                                                        checked={isChecked}
                                                                        style={{ cursor: 'pointer' }}
                                                                        onChange={() => {
                                                                            if (isChecked) {
                                                                                setChartTableColumns(prev => prev.filter(id => id !== col.id));
                                                                            } else {
                                                                                setChartTableColumns(prev => [...prev, col.id]);
                                                                            }
                                                                        }}
                                                                    />
                                                                    <span>{col.label}</span>
                                                                </label>
                                                            );
                                                        })}
                                                    </Box>
                                                </Box>
                                            ) : chartType === 'card' ? (
                                                <>
                                                    {/* Field to Aggregate */}
                                                    <Box>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                                            Field to Aggregate
                                                        </Typography>
                                                        <Select
                                                            options={yAxisOptions}
                                                            value={yAxisOptions.find(o => o.value === chartYAxis)}
                                                            onChange={(opt) => setChartYAxis(opt ? opt.value : '')}
                                                            styles={reactSelectStyles}
                                                            isSearchable={true}
                                                            menuPortalTarget={document.body}
                                                        />
                                                    </Box>

                                                    {/* Aggregation Function */}
                                                    <Box>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                                            Aggregation Function
                                                        </Typography>
                                                        <Select
                                                            options={aggregateOptions}
                                                            value={aggregateOptions.find(o => o.value === chartFunc)}
                                                            onChange={(opt) => setChartFunc(opt ? opt.value : 'sum')}
                                                            styles={reactSelectStyles}
                                                            isSearchable={false}
                                                            menuPortalTarget={document.body}
                                                        />
                                                    </Box>

                                                    {/* Trend Comparison indicator */}
                                                    <Box sx={{ mt: 1, p: 1.5, border: '1px solid #e2e8f0', borderRadius: '8px', bgcolor: '#f8fafc' }}>
                                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: '600', color: '#334155', cursor: 'pointer' }}>
                                                            <input 
                                                                type="checkbox" 
                                                                checked={chartComparisonEnabled}
                                                                onChange={(e) => setChartComparisonEnabled(e.target.checked)}
                                                                style={{ cursor: 'pointer' }}
                                                            />
                                                            Show Comparison Trend Indicator
                                                        </label>
                                                        {chartComparisonEnabled && (
                                                            <Box sx={{ mt: 1.5 }}>
                                                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                                                    Compare Granularity
                                                                </Typography>
                                                                <select
                                                                    value={chartComparisonPeriod}
                                                                    onChange={(e) => setChartComparisonPeriod(e.target.value)}
                                                                    style={{
                                                                        width: '100%',
                                                                        padding: '6px 10px',
                                                                        borderRadius: '6px',
                                                                        border: '1px solid rgba(0,0,0,0.2)',
                                                                        fontSize: '12px',
                                                                        outline: 'none',
                                                                        boxSizing: 'border-box'
                                                                    }}
                                                                >
                                                                    <option value="month">Compare vs Previous Month</option>
                                                                    <option value="year">Compare vs Previous Year</option>
                                                                </select>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </>
                                            ) : (
                                                <>
                                                    {/* Category Dimension (X-Axis) */}
                                                    <Box>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                                            Category Dimension (X-Axis)
                                                        </Typography>
                                                        <Select
                                                            options={xAxisOptions}
                                                            value={xAxisOptions.find(o => o.value === chartXAxis)}
                                                            onChange={(opt) => setChartXAxis(opt ? opt.value : '')}
                                                            styles={reactSelectStyles}
                                                            isSearchable={true}
                                                            menuPortalTarget={document.body}
                                                        />
                                                    </Box>

                                                    {/* Metric Target (Y-Axis) */}
                                                    <Box>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                                            Metric Target (Y-Axis)
                                                        </Typography>
                                                        <Select
                                                            options={yAxisOptions}
                                                            value={yAxisOptions.find(o => o.value === chartYAxis)}
                                                            onChange={(opt) => setChartYAxis(opt ? opt.value : '')}
                                                            styles={reactSelectStyles}
                                                            isSearchable={true}
                                                            menuPortalTarget={document.body}
                                                        />
                                                    </Box>

                                                    {/* Aggregation Function */}
                                                    <Box>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                                            Aggregation Function
                                                        </Typography>
                                                        <Select
                                                            options={aggregateOptions}
                                                            value={aggregateOptions.find(o => o.value === chartFunc)}
                                                            onChange={(opt) => setChartFunc(opt ? opt.value : 'sum')}
                                                            styles={reactSelectStyles}
                                                            isSearchable={false}
                                                            menuPortalTarget={document.body}
                                                        />
                                                    </Box>

                                                    {/* Dual Axis (Combo Chart) */}
                                                    {['bar', 'horizontalBar', 'line'].includes(chartType) && (
                                                        <Box sx={{ mt: 1, p: 1.5, border: '1px solid #e2e8f0', borderRadius: '8px', bgcolor: '#f8fafc' }}>
                                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: '600', color: '#334155', cursor: 'pointer' }}>
                                                                <input 
                                                                    type="checkbox" 
                                                                    checked={chartSecondaryYAxisEnabled}
                                                                    onChange={(e) => setChartSecondaryYAxisEnabled(e.target.checked)}
                                                                    style={{ cursor: 'pointer' }}
                                                                />
                                                                Enable Dual Axis (Combo Chart)
                                                            </label>
                                                            {chartSecondaryYAxisEnabled && (
                                                                <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                                    <Box>
                                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                                                            Secondary Metric (Line Graph)
                                                                        </Typography>
                                                                        <Select
                                                                            options={yAxisOptions}
                                                                            value={yAxisOptions.find(o => o.value === chartSecondaryYAxisCol)}
                                                                            onChange={(opt) => setChartSecondaryYAxisCol(opt ? opt.value : 'project_id')}
                                                                            styles={reactSelectStyles}
                                                                            isSearchable={true}
                                                                            menuPortalTarget={document.body}
                                                                        />
                                                                    </Box>
                                                                    <Box>
                                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                                                            Secondary Aggregation
                                                                        </Typography>
                                                                        <Select
                                                                            options={aggregateOptions}
                                                                            value={aggregateOptions.find(o => o.value === chartSecondaryYAxisFunc)}
                                                                            onChange={(opt) => setChartSecondaryYAxisFunc(opt ? opt.value : 'count_distinct')}
                                                                            styles={reactSelectStyles}
                                                                            isSearchable={false}
                                                                            menuPortalTarget={document.body}
                                                                        />
                                                                    </Box>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}

                                    {modalTab === 'filters' && (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                                    Configure Local Query Filters
                                                </Typography>
                                                <Button 
                                                    size="small" 
                                                    variant="text" 
                                                    onClick={() => setChartFilters(prev => [...prev, { relation: 'AND', column: 'roleName', operator: 'equals', value: '' }])}
                                                    sx={{ fontSize: '11px', textTransform: 'none', py: 0 }}
                                                >
                                                    + Add Condition
                                                </Button>
                                            </Box>
                                            
                                            {chartFilters.length === 0 ? (
                                                <Box sx={{ py: 3, textAlign: 'center', border: '1px dashed #cbd5e1', borderRadius: '8px' }}>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                        No local filters configured. This chart will follow global filters.
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                chartFilters.map((filter, index) => (
                                                    <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                            {index > 0 ? (
                                                                <Box sx={{ width: 80 }}>
                                                                    <select
                                                                        value={filter.relation || 'AND'}
                                                                        onChange={(e) => {
                                                                            const val = e.target.value;
                                                                            setChartFilters(prev => prev.map((f, i) => i === index ? { ...f, relation: val } : f));
                                                                        }}
                                                                        style={{
                                                                            width: '100%',
                                                                            padding: '4px 6px',
                                                                            borderRadius: '4px',
                                                                            border: '1px solid rgba(0,0,0,0.15)',
                                                                            fontSize: '11px',
                                                                            fontWeight: '600',
                                                                            outline: 'none',
                                                                            boxSizing: 'border-box',
                                                                            height: '28px',
                                                                            backgroundColor: '#f1f5f9'
                                                                        }}
                                                                    >
                                                                        <option value="AND">AND</option>
                                                                        <option value="OR">OR</option>
                                                                    </select>
                                                                </Box>
                                                            ) : (
                                                                <Box sx={{ width: 80 }}>
                                                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', pl: 1 }}>
                                                                        Where
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                            
                                                            <Box sx={{ flex: 1 }}>
                                                                <Select
                                                                    options={filterColumnOptions}
                                                                    value={filterColumnOptions.find(o => o.value === filter.column)}
                                                                    onChange={(opt) => {
                                                                        setChartFilters(prev => prev.map((f, i) => i === index ? { ...f, column: opt ? opt.value : '' } : f));
                                                                    }}
                                                                    styles={reactSelectStyles}
                                                                    isSearchable={true}
                                                                    menuPortalTarget={document.body}
                                                                />
                                                            </Box>
                                                            
                                                            <IconButton 
                                                                size="small" 
                                                                onClick={() => setChartFilters(prev => prev.filter((_, i) => i !== index))}
                                                                sx={{ color: '#ef4444' }}
                                                            >
                                                                <DeleteIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Box>
                                                        
                                                        <Box sx={{ display: 'flex', gap: 1, pl: '80px', alignItems: 'center' }}>
                                                            <Box sx={{ width: 110 }}>
                                                                <select
                                                                    value={filter.operator || 'equals'}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value;
                                                                        setChartFilters(prev => prev.map((f, i) => i === index ? { ...f, operator: val } : f));
                                                                    }}
                                                                    style={{
                                                                        width: '100%',
                                                                        padding: '4px 6px',
                                                                        borderRadius: '4px',
                                                                        border: '1px solid rgba(0,0,0,0.15)',
                                                                        fontSize: '11px',
                                                                        outline: 'none',
                                                                        boxSizing: 'border-box',
                                                                        height: '28px',
                                                                        backgroundColor: 'white'
                                                                    }}
                                                                >
                                                                    {filterOperatorOptions.map(op => (
                                                                        <option key={op.value} value={op.value}>{op.label}</option>
                                                                    ))}
                                                                </select>
                                                            </Box>
                                                            <Box sx={{ flex: 1 }}>
                                                                <input
                                                                    type="text"
                                                                    placeholder={(filter.operator || '').includes('list') ? "Comma-separated values" : "Value"}
                                                                    value={filter.value || ''}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value;
                                                                        setChartFilters(prev => prev.map((f, i) => i === index ? { ...f, value: val } : f));
                                                                    }}
                                                                    style={{
                                                                        width: '100%',
                                                                        padding: '6px 10px',
                                                                        borderRadius: '4px',
                                                                        border: '1px solid rgba(0,0,0,0.15)',
                                                                        fontSize: '11px',
                                                                        outline: 'none',
                                                                        boxSizing: 'border-box',
                                                                        height: '28px'
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                ))
                                            )}
                                        </Box>
                                    )}

                                    {modalTab === 'formatting' && (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                                    Conditional Alert Rules (Highlight Cells)
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    onClick={() => setChartConditionalRules(prev => [...prev, { column: 'total', operator: '>', target: '', bgColor: '#dcfce7', textColor: '#15803d' }])}
                                                    sx={{ fontSize: '11px', textTransform: 'none', py: 0 }}
                                                >
                                                    + Add Rule
                                                </Button>
                                            </Box>

                                            {chartConditionalRules.length === 0 ? (
                                                <Box sx={{ py: 3, textAlign: 'center', border: '1px dashed #cbd5e1', borderRadius: '8px' }}>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                        No conditional formatting rules configured.
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                chartConditionalRules.map((rule, ruleIndex) => (
                                                    <Box key={ruleIndex} sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: '8px', bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>
                                                                Rule #{ruleIndex + 1}
                                                            </Typography>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => setChartConditionalRules(prev => prev.filter((_, idx) => idx !== ruleIndex))}
                                                                sx={{ color: '#ef4444' }}
                                                            >
                                                                <DeleteIcon sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        </Box>

                                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                            <Box sx={{ flex: 1 }}>
                                                                <select
                                                                    value={rule.column}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value;
                                                                        setChartConditionalRules(prev => prev.map((r, i) => i === ruleIndex ? { ...r, column: val } : r));
                                                                    }}
                                                                    style={{ width: '100%', padding: '4px 6px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '11px', height: '28px' }}
                                                                >
                                                                    {AVAILABLE_COLUMNS.filter(c => ['qty', 'total', 'unit', 'mrp', 'total_list_price'].includes(c.id)).map(c => (
                                                                        <option key={c.id} value={c.id}>{c.label}</option>
                                                                    ))}
                                                                </select>
                                                            </Box>
                                                            <Box sx={{ width: 80 }}>
                                                                <select
                                                                    value={rule.operator}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value;
                                                                        setChartConditionalRules(prev => prev.map((r, i) => i === ruleIndex ? { ...r, operator: val } : r));
                                                                    }}
                                                                    style={{ width: '100%', padding: '4px 6px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '11px', height: '28px' }}
                                                                >
                                                                    <option value=">">&gt;</option>
                                                                    <option value="<">&lt;</option>
                                                                    <option value="=">=</option>
                                                                </select>
                                                            </Box>
                                                            <Box sx={{ flex: 1 }}>
                                                                <input
                                                                    type="number"
                                                                    placeholder="Target"
                                                                    value={rule.target}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value;
                                                                        setChartConditionalRules(prev => prev.map((r, i) => i === ruleIndex ? { ...r, target: val } : r));
                                                                    }}
                                                                    style={{ width: '100%', padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '11px', height: '28px', boxSizing: 'border-box' }}
                                                                />
                                                            </Box>
                                                        </Box>

                                                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 0.5 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Typography variant="caption" sx={{ fontSize: '10px', color: '#64748b' }}>
                                                                    BG Color:
                                                                </Typography>
                                                                <input 
                                                                    type="color" 
                                                                    value={rule.bgColor} 
                                                                    onChange={(e) => {
                                                                        const val = e.target.value;
                                                                        setChartConditionalRules(prev => prev.map((r, i) => i === ruleIndex ? { ...r, bgColor: val } : r));
                                                                    }}
                                                                    style={{ border: 'none', padding: 0, width: '22px', height: '22px', cursor: 'pointer', backgroundColor: 'transparent' }}
                                                                />
                                                            </Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Typography variant="caption" sx={{ fontSize: '10px', color: '#64748b' }}>
                                                                    Text Color:
                                                                </Typography>
                                                                <input 
                                                                    type="color" 
                                                                    value={rule.textColor} 
                                                                    onChange={(e) => {
                                                                        const val = e.target.value;
                                                                        setChartConditionalRules(prev => prev.map((r, i) => i === ruleIndex ? { ...r, textColor: val } : r));
                                                                    }}
                                                                    style={{ border: 'none', padding: 0, width: '22px', height: '22px', cursor: 'pointer', backgroundColor: 'transparent' }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                ))
                                            )}
                                        </Box>
                                    )}
                                </Box>

                                    {/* Action Buttons */}
                                    <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5 }}>
                                        <Button
                                            variant="outlined"
                                            onClick={() => setIsChartModalOpen(false)}
                                            fullWidth
                                            sx={{ textTransform: "none", py: 1, borderRadius: 2, fontWeight: 600 }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="contained"
                                            onClick={handleSaveChart}
                                            fullWidth
                                            sx={{ textTransform: "none", py: 1, borderRadius: 2, fontWeight: 600, bgcolor: "#4f46e5", "&:hover": { bgcolor: "#4338ca" } }}
                                        >
                                            {editingChart ? "Save Changes" : "Create Chart"}
                                        </Button>
                                    </Box>
                            </Card>
                        </Fade>
                    </Backdrop>
                </Portal>
            )}

        </MainLayout>
    );
};

export default AdminDynamicDashboard;

// ==========================================
// INDEPENDENT CUSTOM TABLE CARD COMPONENT
// ==========================================
const DashboardTableCard = ({ 
    chart, 
    currentFilters, 
    onEdit, 
    onDelete, 
    roleOpt,
    AVAILABLE_COLUMNS,
    DEFAULT_COLUMNS,
    onColumnsChange
}) => {
    const getCellStyles = (row, key) => {
        const rules = chart.conditionalRules || [];
        const value = Number(row[key]);
        if (isNaN(value)) return {};

        for (const rule of rules) {
            if (rule.column === key) {
                const target = Number(rule.target);
                if (isNaN(target)) continue;

                let matches = false;
                if (rule.operator === '>') matches = value > target;
                else if (rule.operator === '<') matches = value < target;
                else if (rule.operator === '=') matches = value === target;

                if (matches) {
                    return {
                        backgroundColor: rule.bgColor || '#dcfce7',
                        color: rule.textColor || '#15803d',
                        fontWeight: 'bold'
                    };
                }
            }
        }
        return {};
    };

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [sortBy, setSortBy] = useState({ sort_by: "created_at", order: "desc" });
    const [tableData, setTableData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);

    const [isColSelectorOpen, setIsColSelectorOpen] = useState(false);
    const [isColSelectorClicked, setIsColSelectorClicked] = useState(false);
    const [colSearchQuery, setColSearchQuery] = useState("");
    const colSelectorRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (colSelectorRef.current && !colSelectorRef.current.contains(event.target)) {
                setIsColSelectorOpen(false);
                setIsColSelectorClicked(false);
                setColSearchQuery("");
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedColumns = chart.columns || DEFAULT_COLUMNS;
    
    // Fetch data for this table card
    useEffect(() => {
        const controller = new AbortController();
        const fetchData = async () => {
            if (selectedColumns.length === 0) {
                setTableData([]);
                setTotalRecords(0);
                return;
            }
            setLoading(true);
            try {
                const token = localStorage.getItem("u_token");
                const response = await axios.post(`${Apiurl}admin/dynamic-dashboard/data`, {
                    columns: selectedColumns,
                    page: page + 1,
                    limit: rowsPerPage,
                    sort_by: sortBy.sort_by,
                    sort_order: sortBy.order,
                    charts: [],
                    ...currentFilters
                }, {
                    headers: { Authorization: `Bearer ${token}` },
                    signal: controller.signal
                });

                if (response.data.status) {
                    setTableData(response.data.data ? response.data.data.data : []);
                    setTotalRecords(response.data.data ? response.data.data.total : 0);
                }
            } catch (error) {
                if (axios.isCancel(error) || error.name === 'CanceledError') {
                    console.log("Card Table request aborted");
                } else {
                    console.error("Error fetching card table data", error);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchData();
        return () => controller.abort();
    }, [JSON.stringify(selectedColumns), page, rowsPerPage, JSON.stringify(sortBy), JSON.stringify(currentFilters)]);

    // Handle Local excel download for this table
    const [isDownloading, setIsDownloading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [downloadTotal, setDownloadTotal] = useState(0);
    const [extractedCount, setExtractedCount] = useState(0);
    const [estimatedTime, setEstimatedTime] = useState("Calculating...");
    const [startTime, setStartTime] = useState(null);
    const downloadAbortControllerRef = useRef(null);

    // Calculate remaining time for this card download
    useEffect(() => {
        if (isDownloading && startTime && extractedCount > 0 && downloadTotal > 0) {
            const elapsedSeconds = (Date.now() - startTime) / 1000;
            const speed = extractedCount / elapsedSeconds;
            if (speed > 0) {
                const remaining = downloadTotal - extractedCount;
                const secondsRemaining = Math.ceil(remaining / speed);
                if (secondsRemaining <= 0) {
                    setEstimatedTime("Finishing up...");
                } else if (secondsRemaining < 60) {
                    setEstimatedTime(`${secondsRemaining} seconds`);
                } else if (secondsRemaining < 3600) {
                    setEstimatedTime(`${Math.ceil(secondsRemaining / 60)} minutes`);
                } else {
                    setEstimatedTime(`${Math.ceil(secondsRemaining / 3600)} hours`);
                }
            }
        }
    }, [isDownloading, startTime, extractedCount, downloadTotal]);

    const handleDownloadExcel = async () => {
        setIsDownloading(true);
        setProgress(0);
        setExtractedCount(0);
        setDownloadTotal(0);
        setEstimatedTime("Calculating...");
        setStartTime(Date.now());
        downloadAbortControllerRef.current = new AbortController();

        const LIMIT = 5000;
        let allDownloadedData = [];
        const token = localStorage.getItem("u_token");

        try {
            let pageIdx = 1;
            let hasMore = true;

            while (hasMore) {
                const response = await axios.post(
                    `${Apiurl}admin/dynamic-dashboard/data`,
                    {
                        columns: selectedColumns,
                        page: pageIdx,
                        limit: LIMIT,
                        sort_by: sortBy.sort_by,
                        sort_order: sortBy.order,
                        ...currentFilters
                    },
                    {
                        signal: downloadAbortControllerRef.current.signal,
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );

                const pageData = response.data?.data?.data || [];
                const returnedTotal = response.data?.data?.total || 0;

                if (pageIdx === 1) {
                    setDownloadTotal(returnedTotal);
                }

                allDownloadedData = [...allDownloadedData, ...pageData];
                setExtractedCount(allDownloadedData.length);

                if (returnedTotal > 0) {
                    setProgress(Math.min(99, Math.round((allDownloadedData.length / returnedTotal) * 100)));
                }

                if (pageData.length < LIMIT || allDownloadedData.length >= returnedTotal) {
                    hasMore = false;
                } else {
                    pageIdx++;
                }
            }

            const headings = selectedColumns.map(colId => {
                const col = AVAILABLE_COLUMNS.find(c => c.id === colId);
                return col ? col.label : colId;
            });

            const sheetData = [
                ["SL No.", ...headings],
                ...allDownloadedData.map((row, idx) => [
                    idx + 1,
                    ...selectedColumns.map(colId => {
                        if (['mrp', 'unit', 'total', 'total_list_price', 'qty'].includes(colId)) {
                            return row[colId] !== undefined && row[colId] !== null ? Number(row[colId]) : 0;
                        }
                        if (colId === 'created_at') {
                            return row.created_at ? new Date(row.created_at).toLocaleString("en-GB") : "N/A";
                        }
                        return row[colId] || "N/A";
                    })
                ])
            ];

            const ws = XLSX.utils.aoa_to_sheet(sheetData);
            const wscols = [{ wch: 8 }, ...selectedColumns.map(() => ({ wch: 20 }))];
            ws["!cols"] = wscols;

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Card Table Report");

            const timestamp = new Date().toISOString().split("T")[0];
            const cleanTitle = (chart.title || "table").replace(/[^a-z0-9]/gi, '_').toLowerCase();
            const fileName = `Table_${cleanTitle}_${timestamp}.xlsx`;
            XLSX.writeFile(wb, fileName);

            Swal.fire({
                title: "Export Successful!",
                html: `<div style="text-align: left; padding: 10px;">
                    <p><strong>Table:</strong> ${chart.title}</p>
                    <p><strong>Records exported:</strong> ${allDownloadedData.length.toLocaleString()}</p>
                    <p><strong>File name:</strong> ${fileName}</p>
                  </div>`,
                icon: "success",
                confirmButtonColor: "#10b981",
                confirmButtonText: "OK",
            });
        } catch (error) {
            if (!axios.isCancel(error)) {
                console.error("Error exporting table card data", error);
                Swal.fire({
                    title: "Export Failed",
                    text: error.message || "Failed to fetch data for export. Please try again.",
                    icon: "error",
                    confirmButtonColor: "#ef4444",
                });
            }
        } finally {
            setIsDownloading(false);
            setProgress(0);
            setDownloadTotal(0);
            setEstimatedTime("Calculating...");
            setStartTime(null);
        }
    };

    const handleSort = (columnKey) => {
        setSortBy(prev => ({
            sort_by: columnKey,
            order: prev.sort_by === columnKey && prev.order === "asc" ? "desc" : "asc"
        }));
    };

    const renderCell = (row, key) => {
        if (row[key] === undefined) {
            return <span className="inline-block w-16 h-3 bg-gray-200 animate-pulse rounded" />;
        }
        if (key === 'sku_code') {
            return <ClickableTableLink path="/admin/product" displayValue={row.sku_code} searchValue={row.sku_code} />;
        }
        if (key === 'project_id') {
            return <ClickableTableLink path="/admin/projects" displayValue={row.project_id} searchValue={row.project_id} />;
        }
        if (key === 'client_name') {
            return <ClickableTableLink path="/admin/clientlist" displayValue={row.client_name} searchValue={row.client_name} />;
        }
        if (key === 'user_full_name') {
            return <ClickableTableLink path="/admin/userlist" displayValue={row.user_full_name} searchValue={row.user_full_name} />;
        }
        if (key === 'roleName') {
            const match = roleOpt.find(r => r.label === row.roleName);
            return <ClickableTableLink path="/admin/userlist" displayValue={row.roleName} searchValue="" role={match ? match.value : undefined} />;
        }
        if (['mrp', 'unit', 'total', 'total_list_price'].includes(key)) {
            return row[key] !== undefined && row[key] !== null 
                ? Number(row[key]).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) 
                : "N/A";
        }
        if (key === 'created_at') {
            return row.created_at ? new Date(row.created_at).toLocaleString("en-GB") : "N/A";
        }
        return row[key] || "N/A";
    };

    return (
        <Paper sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden', bgcolor: 'white', borderRadius: 3, border: "1px solid rgba(0,0,0,0.08)", boxShadow: "0 4px 12px rgba(0,0,0,0.05)", position: 'relative' }}>
            {/* Table Header Bar */}
            <Box sx={{ px: 2, py: 1.5, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TableChartIcon sx={{ color: "#64748b", fontSize: 18 }} />
                    <Typography sx={{ fontSize: '11px', fontWeight: 800, color: '#1e293b', textTransform: 'uppercase', letterSpacing: 1 }}>
                        {chart.title}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {/* Local Column Selector Dropdown */}
                    <Box 
                        ref={colSelectorRef}
                        onMouseEnter={() => setIsColSelectorOpen(true)}
                        onMouseLeave={() => {
                            if (!isColSelectorClicked) {
                                setIsColSelectorOpen(false);
                                setColSearchQuery("");
                            }
                        }}
                        sx={{ position: 'relative', display: 'inline-block' }}
                    >
                        <Tooltip title="Select Columns" arrow>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    if (isColSelectorClicked) {
                                        setIsColSelectorClicked(false);
                                        setIsColSelectorOpen(false);
                                        setColSearchQuery("");
                                    } else {
                                        setIsColSelectorClicked(true);
                                        setIsColSelectorOpen(true);
                                    }
                                }}
                                sx={{ border: '1px solid #cbd5e1', bgcolor: 'white', '&:hover': { bgcolor: '#f1f5f9' } }}
                            >
                                <TuneRoundedIcon sx={{ fontSize: 13, color: '#0ea5e9' }} />
                            </IconButton>
                        </Tooltip>

                        {isColSelectorOpen && (
                            <Box 
                                sx={{ 
                                    position: 'absolute', 
                                    right: 0, 
                                    top: '100%', 
                                    mt: 1, 
                                    bgcolor: 'white', 
                                    border: '1px solid rgba(0,0,0,0.15)', 
                                    borderRadius: 2, 
                                    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.15)',
                                    width: '240px',
                                    zIndex: 1000,
                                    overflow: 'hidden',
                                    p: 1.2
                                }}
                            >
                                <input
                                    type="text"
                                    placeholder="Search columns..."
                                    value={colSearchQuery}
                                    onChange={(e) => setColSearchQuery(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '6px 10px',
                                        borderRadius: '6px',
                                        border: '1px solid rgba(0,0,0,0.2)',
                                        fontSize: '11px',
                                        outline: 'none',
                                        marginBottom: '8px',
                                        boxSizing: 'border-box'
                                    }}
                                />

                                <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {AVAILABLE_COLUMNS.filter(col => 
                                        col.label.toLowerCase().includes(colSearchQuery.toLowerCase())
                                    ).map(col => {
                                        const isSelected = selectedColumns.includes(col.id);
                                        const orderIndex = selectedColumns.indexOf(col.id);
                                        
                                        return (
                                            <Box
                                                key={col.id}
                                                onClick={() => {
                                                    let newCols;
                                                    if (isSelected) {
                                                        newCols = selectedColumns.filter(id => id !== col.id);
                                                    } else {
                                                        newCols = [...selectedColumns, col.id];
                                                    }
                                                    onColumnsChange(newCols);
                                                }}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    padding: '6px 8px',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '11px',
                                                    color: isSelected ? '#0284c7' : 'rgba(0,0,0,0.85)',
                                                    backgroundColor: isSelected ? 'rgba(56, 189, 248, 0.08)' : 'transparent',
                                                    marginBottom: '2px',
                                                    transition: 'all 0.1s ease',
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box
                                                        sx={{
                                                            width: 12,
                                                            height: 12,
                                                            borderRadius: '3px',
                                                            border: isSelected ? '1px solid #0ea5e9' : '1px solid rgba(0,0,0,0.3)',
                                                            bgcolor: isSelected ? '#0ea5e9' : 'transparent',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: 'white',
                                                            fontSize: '9px'
                                                        }}
                                                    >
                                                        {isSelected && "✓"}
                                                    </Box>
                                                    <span>{col.label}</span>
                                                </Box>
                                                
                                                {isSelected && (
                                                    <Box 
                                                        sx={{ 
                                                            bgcolor: '#0284c7', 
                                                            color: 'white', 
                                                            width: 14, 
                                                            height: 14, 
                                                            borderRadius: '50%', 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            justifyContent: 'center',
                                                            fontSize: '8px',
                                                            fontWeight: 700 
                                                        }}
                                                    >
                                                        {orderIndex + 1}
                                                    </Box>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        )}
                    </Box>

                    <Tooltip title="Download Excel" arrow>
                        <IconButton
                            size="small"
                            disabled={isDownloading || totalRecords === 0}
                            onClick={handleDownloadExcel}
                            sx={{ border: '1px solid #cbd5e1', bgcolor: 'white', '&:hover': { bgcolor: '#f1f5f9' } }}
                        >
                            <DownloadIcon sx={{ fontSize: 13, color: '#10b981' }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Table" arrow>
                        <IconButton
                            size="small"
                            onClick={onEdit}
                            sx={{ border: '1px solid #cbd5e1', bgcolor: 'white', '&:hover': { bgcolor: '#f1f5f9' } }}
                        >
                            <EditIcon sx={{ fontSize: 13, color: '#4f46e5' }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Table" arrow>
                        <IconButton
                            size="small"
                            onClick={onDelete}
                            sx={{ border: '1px solid #cbd5e1', bgcolor: 'white', '&:hover': { bgcolor: '#fee2e2' } }}
                        >
                            <DeleteIcon sx={{ fontSize: 13, color: '#ef4444' }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Data Table */}
            <TableContainer sx={{ flexGrow: 1, overflowY: "auto", overflowX: "auto", minHeight: 280 }}>
                <Table stickyHeader size="small" sx={{ minWidth: Math.max(1200, selectedColumns.length * 165) }}>
                    <TableHead>
                        <TableRow>
                            {selectedColumns.length > 0 && (
                                <TableCell sx={{ bgcolor: "#04121f", color: "white", fontWeight: 700, fontSize: "11px", borderBottom: "1px solid rgba(255,255,255,0.08)", whiteSpace: "nowrap" }}>SL No.</TableCell>
                            )}
                            {selectedColumns.map(colId => {
                                const col = AVAILABLE_COLUMNS.find(c => c.id === colId);
                                if (!col) return null;
                                return (
                                    <TableCell 
                                        key={col.id}
                                        sx={{ 
                                            bgcolor: "#04121f", 
                                            color: "white", 
                                            fontWeight: 700, 
                                            fontSize: "11px",
                                            borderBottom: "1px solid rgba(255,255,255,0.08)",
                                            whiteSpace: "nowrap"
                                        }}
                                    >
                                        <TableSortLabel
                                            active={sortBy.sort_by === col.id}
                                            direction={sortBy.sort_by === col.id ? sortBy.order : "desc"}
                                            onClick={() => handleSort(col.id)}
                                            hideSortIcon={false}
                                            sx={{
                                                color: "white !important",
                                                "& .MuiTableSortLabel-icon": { color: "#38bdf8 !important", opacity: "1 !important" }
                                            }}
                                        >
                                            {col.label}
                                        </TableSortLabel>
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            Array.from({ length: 5 }).map((_, rIdx) => (
                                <TableRow key={`skeleton-row-${rIdx}`} sx={{ bgcolor: rIdx % 2 === 0 ? "white" : "rgba(4, 18, 31, 0.015)" }}>
                                    {selectedColumns.length > 0 && (
                                        <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.06)", py: 1.5 }}>
                                            <Skeleton variant="rectangular" width="40%" height={12} sx={{ bgcolor: 'rgba(0,0,0,0.04)', borderRadius: 0.5 }} />
                                        </TableCell>
                                    )}
                                    {selectedColumns.map((colId, cIdx) => (
                                        <TableCell key={`skeleton-cell-${rIdx}-${cIdx}`} sx={{ borderBottom: "1px solid rgba(0,0,0,0.06)", py: 1.5 }}>
                                            <Skeleton variant="rectangular" width={cIdx % 2 === 0 ? "70%" : "55%"} height={12} sx={{ bgcolor: 'rgba(0,0,0,0.04)', borderRadius: 0.5 }} />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : tableData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={selectedColumns.length + 1} align="center" sx={{ py: 8, color: "rgba(0,0,0,0.4)", fontStyle: "italic" }}>
                                    No quotation records found matching the active filters.
                                </TableCell>
                            </TableRow>
                        ) : (
                            tableData.map((row, index) => (
                                <TableRow 
                                    key={`table-row-${index}`}
                                    sx={{ 
                                        bgcolor: index % 2 === 0 ? "white" : "rgba(4, 18, 31, 0.015)",
                                        "&:hover": { bgcolor: "rgba(4, 18, 31, 0.03)" }
                                    }}
                                >
                                    {selectedColumns.length > 0 && (
                                        <TableCell sx={{ color: "rgba(0,0,0,0.5)", borderBottom: "1px solid rgba(0,0,0,0.08)", fontSize: "11px", fontWeight: 600 }}>
                                            {page * rowsPerPage + index + 1}
                                        </TableCell>
                                    )}
                                    {selectedColumns.map(colId => {
                                        const cellStyles = getCellStyles(row, colId);
                                        return (
                                            <TableCell 
                                                key={colId} 
                                                sx={{ 
                                                    borderBottom: "1px solid rgba(0,0,0,0.08)", 
                                                    fontSize: "11px", 
                                                    color: cellStyles.color || "rgba(0,0,0,0.8)",
                                                    bgcolor: cellStyles.backgroundColor || "transparent",
                                                    fontWeight: cellStyles.fontWeight || "normal"
                                                }}
                                            >
                                                {renderCell(row, colId)}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
                rowsPerPageOptions={[15, 25, 50, 100]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                }}
                sx={{ 
                    color: "rgba(0,0,0,0.7)",
                    ".MuiTablePagination-selectIcon": { color: "rgba(0,0,0,0.7)" },
                    borderTop: "1px solid rgba(0,0,0,0.08)",
                    bgcolor: "#f8fafc"
                }}
            />

            {/* Export Overlay progress */}
            {isDownloading && (
                <Portal>
                    <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 99999, display: 'flex', flexDirection: 'column', gap: 2, bgcolor: 'rgba(4, 18, 31, 0.8)' }} open={true}>
                        <Card sx={{ p: 4, width: 420, borderRadius: 4, display: 'flex', flexDirection: 'column', gap: 2, boxShadow: '0 20px 50px rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="h6" fontWeight={700} color="#1e293b">Exporting Table...</Typography>
                                <Typography variant="caption" sx={{ px: 1.5, py: 0.5, bgcolor: '#e0f2fe', color: '#0369a1', borderRadius: 99, fontWeight: 700 }}>
                                    {progress}%
                                </Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: '#10b981' } }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Rows Downloaded:</Typography>
                                    <Typography variant="body2" fontWeight={600} color="text.primary">
                                        {extractedCount.toLocaleString()} {downloadTotal > 0 ? `of ${downloadTotal.toLocaleString()}` : ""}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Estimated Time:</Typography>
                                    <Typography variant="body2" fontWeight={600} color="text.primary">{estimatedTime}</Typography>
                                </Box>
                            </Box>
                            <Button variant="outlined" color="error" size="small" onClick={() => downloadAbortControllerRef.current?.abort()} sx={{ mt: 1, textTransform: 'none', borderRadius: 2 }}>
                                Cancel Export
                            </Button>
                        </Card>
                    </Backdrop>
                </Portal>
            )}
        </Paper>
    );
};


// ==========================================
// INDEPENDENT CUSTOM CHART CARD COMPONENT
// ==========================================
const DashboardChartCard = ({
    chart,
    currentFilters,
    onEdit,
    onDelete,
    onDownload,
    isDownloading,
    chartHeight,
    onGlobalFilterApply,
    onGlobalFilterClear
}) => {
    const [activeConfig, setActiveConfig] = useState(chart);
    const [drilldownHistory, setDrilldownHistory] = useState([]);
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setActiveConfig(chart);
        setDrilldownHistory([]);
    }, [JSON.stringify(chart)]);

    useEffect(() => {
        const controller = new AbortController();
        const fetchChartData = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem("u_token");
                const response = await axios.post(`${Apiurl}admin/dynamic-dashboard/data`, {
                    columns: [], 
                    charts: [activeConfig],
                    ...currentFilters
                }, {
                    headers: { Authorization: `Bearer ${token}` },
                    signal: controller.signal
                });

                if (response.data.status) {
                    const data = response.data.chartsData || {};
                    setChartData(data[activeConfig.id] || null);
                }
            } catch (error) {
                if (!axios.isCancel(error) && error.name !== 'CanceledError') {
                    console.error("Error fetching chart data", error);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchChartData();
        return () => controller.abort();
    }, [JSON.stringify(activeConfig), JSON.stringify(currentFilters)]);

    const DRILLDOWN_MAP = {
        'client_region': 'client_state',
        'client_state': 'client_name',
        'user_region': 'user_state',
        'user_state': 'user_full_name',
        'category': 'brand',
        'brand': 'collection',
        'created_at_year': 'created_at_month',
        'created_at_month': 'created_at_day'
    };

    const handleChartClick = (clickedLabel) => {
        if (!clickedLabel || clickedLabel === 'Others') return;

        const currentX = activeConfig.xAxisColumn || 'category';
        const nextX = DRILLDOWN_MAP[currentX];

        if (nextX) {
            setDrilldownHistory(prev => [...prev, {
                label: clickedLabel,
                xAxisColumn: currentX,
                filters: activeConfig.filters || []
            }]);

            const newFilters = [
                ...(activeConfig.filters || []),
                {
                    relation: 'AND',
                    column: currentX,
                    operator: 'equals',
                    value: clickedLabel
                }
            ];

            setActiveConfig(prev => ({
                ...prev,
                xAxisColumn: nextX,
                filters: newFilters
            }));
        }

        if (onGlobalFilterApply) {
            onGlobalFilterApply(currentX, clickedLabel);
        }
    };

    const handleBreadcrumbClick = (idx) => {
        if (idx === -1) {
            drilldownHistory.forEach(step => {
                if (onGlobalFilterClear) {
                    onGlobalFilterClear(step.xAxisColumn, step.label);
                }
            });
            setActiveConfig(chart);
            setDrilldownHistory([]);
        } else {
            const stepsToClear = drilldownHistory.slice(idx);
            stepsToClear.forEach(step => {
                if (onGlobalFilterClear) {
                    onGlobalFilterClear(step.xAxisColumn, step.label);
                }
            });
            const targetStep = drilldownHistory[idx];
            const newHistory = drilldownHistory.slice(0, idx);
            setDrilldownHistory(newHistory);
            setActiveConfig(prev => ({
                ...prev,
                xAxisColumn: targetStep.xAxisColumn,
                filters: targetStep.filters
            }));
        }
    };

    const isHeatmap = chart.type === 'heatmap';
    const displayTitle = activeConfig.title || "Custom Chart";

    return (
        <Box sx={{ 
            bgcolor: 'white', 
            borderRadius: '12px', 
            boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.02)', 
            border: '1px solid #e2e8f0', 
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
        }}>
            {/* Unified Card Header */}
            <Box sx={{ 
                px: 2, 
                py: 1.5, 
                bgcolor: '#f8fafc', 
                borderBottom: '1px solid #e2e8f0', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center' 
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TableChartIcon sx={{ fontSize: '1.15rem', color: '#64748b' }} />
                    <Typography sx={{ fontSize: '11px', fontWeight: 'bold', color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {displayTitle}
                    </Typography>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Tooltip title="Download Excel" arrow>
                        <IconButton 
                            disabled={isDownloading || loading || !chartData}
                            size="small" 
                            onClick={onDownload}
                            sx={{ 
                                bgcolor: 'white', 
                                border: '1px solid #cbd5e1', 
                                borderRadius: '6px',
                                p: '4px', 
                                '&:hover': { bgcolor: '#f1f5f9' } 
                            }}
                        >
                            <DownloadIcon sx={{ fontSize: '1rem', color: '#10b981' }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Chart" arrow>
                        <IconButton 
                            size="small" 
                            onClick={onEdit}
                            sx={{ 
                                bgcolor: 'white', 
                                border: '1px solid #cbd5e1', 
                                borderRadius: '6px',
                                p: '4px', 
                                '&:hover': { bgcolor: '#f1f5f9' } 
                            }}
                        >
                            <EditIcon sx={{ fontSize: '1rem', color: '#4f46e5' }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Chart" arrow>
                        <IconButton 
                            size="small" 
                            onClick={onDelete}
                            sx={{ 
                                bgcolor: 'white', 
                                border: '1px solid #cbd5e1', 
                                borderRadius: '6px',
                                p: '4px', 
                                '&:hover': { bgcolor: '#fee2e2', color: '#ef4444' } 
                            }}
                        >
                            <DeleteIcon sx={{ fontSize: '1rem', color: '#ef4444' }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Breadcrumb Trail */}
            {drilldownHistory.length > 0 && (
                <Box sx={{ px: 2, py: 0.75, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                    <Button 
                        size="small" 
                        variant="text" 
                        onClick={() => handleBreadcrumbClick(-1)} 
                        sx={{ fontSize: '10px', textTransform: 'none', py: 0, minWidth: 'auto', px: 0.5, color: '#4f46e5', fontWeight: 600 }}
                    >
                        All
                    </Button>
                    {drilldownHistory.map((step, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '10px' }}>&gt;</Typography>
                            <Button
                                size="small"
                                variant="text"
                                disabled={idx === drilldownHistory.length - 1}
                                onClick={() => handleBreadcrumbClick(idx)}
                                sx={{ 
                                    fontSize: '10px', 
                                    textTransform: 'none', 
                                    py: 0, 
                                    minWidth: 'auto', 
                                    px: 0.5, 
                                    color: idx === drilldownHistory.length - 1 ? '#475569' : '#4f46e5',
                                    fontWeight: idx === drilldownHistory.length - 1 ? 700 : 500 
                                }}
                            >
                                {step.label}
                            </Button>
                        </Box>
                    ))}
                </Box>
            )}

            {/* Content Area */}
            <Box sx={{ p: 2, flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                {isHeatmap ? (
                    <HeatmapChart
                        title={displayTitle}
                        tooltip={`${formatLabel(activeConfig.rowColumn)} vs ${formatLabel(activeConfig.colColumn)} (${formatAgg(activeConfig.aggregateFunction)} of ${formatLabel(activeConfig.valColumn)})`}
                        data={chartData}
                        loading={loading}
                        stretch={true}
                        showColTotals={true}
                        showHeader={false}
                        conditionalRules={activeConfig.conditionalRules || []}
                        valColumn={activeConfig.valColumn}
                    />
                ) : (
                    <DashboardChart
                        title={displayTitle}
                        tooltip={`${formatLabel(activeConfig.xAxisColumn)} vs ${formatLabel(activeConfig.yAxisColumn)} (${formatAgg(activeConfig.aggregateFunction)})`}
                        data={chartData}
                        type={activeConfig.type}
                        loading={loading}
                        height={chartHeight}
                        showHeader={false}
                        onClick={handleChartClick}
                    />
                )}
            </Box>
        </Box>
    );
};


// ==========================================
// INDEPENDENT CUSTOM CARD CARD COMPONENT
// ==========================================
const DashboardCardCard = ({
    chart,
    currentFilters,
    onEdit,
    onDelete,
    onDownload
}) => {
    const [cardData, setCardData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        const fetchCardData = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem("u_token");
                const response = await axios.post(`${Apiurl}admin/dynamic-dashboard/data`, {
                    columns: [], 
                    charts: [chart],
                    ...currentFilters
                }, {
                    headers: { Authorization: `Bearer ${token}` },
                    signal: controller.signal
                });

                if (response.data.status) {
                    const data = response.data.chartsData || {};
                    setCardData(data[chart.id] || null);
                }
            } catch (error) {
                if (!axios.isCancel(error) && error.name !== 'CanceledError') {
                    console.error("Error fetching card data", error);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchCardData();
        return () => controller.abort();
    }, [JSON.stringify(chart), JSON.stringify(currentFilters)]);

    const value = cardData && cardData.value !== undefined ? cardData.value : 0;
    const formattedValue = typeof value === 'number' ? value.toLocaleString() : value;
    
    // Choose color and icon based on yAxisColumn/metric
    const getCardStyle = (col) => {
        switch (col) {
            case 'project_id':
                return {
                    borderColor: '#1e3a8a', // Dark Navy/Blue
                    iconColor: '#3b82f6',
                    bgColor: '#eff6ff',
                    iconBg: '#dbeafe',
                    title: 'Total Quotations',
                    subtitle: 'All-time volume'
                };
            case 'client_name':
                return {
                    borderColor: '#0f766e', // Teal/Green
                    iconColor: '#10b981',
                    bgColor: '#f0fdf4',
                    iconBg: '#d1fae5',
                    title: 'Total Clients',
                    subtitle: 'Total number of clients'
                };
            default:
                return {
                    borderColor: '#6b21a8', // Purple
                    iconColor: '#8b5cf6',
                    bgColor: '#faf5ff',
                    iconBg: '#f3e8ff',
                    title: formatLabel(col),
                    subtitle: 'Filtered volume'
                };
        }
    };

    const style = getCardStyle(chart.yAxisColumn);

    return (
        <Card sx={{ 
            bgcolor: 'white', 
            borderRadius: '12px', 
            boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.02)', 
            border: '1px solid #e2e8f0', 
            borderTop: `4px solid ${style.borderColor}`,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            p: 2,
            height: '140px',
            boxSizing: 'border-box'
        }}>
            {/* Header controls overlay */}
            <Box sx={{ 
                position: 'absolute', 
                top: 8, 
                right: 8, 
                display: 'flex', 
                gap: 0.5 
            }}>
                <IconButton 
                    size="small" 
                    onClick={onDownload}
                    sx={{ 
                        bgcolor: 'rgba(255,255,255,0.8)', 
                        border: '1px solid #e2e8f0', 
                        borderRadius: '6px',
                        p: '3px',
                        '&:hover': { bgcolor: '#f1f5f9' } 
                    }}
                >
                    <DownloadIcon sx={{ fontSize: '0.85rem', color: '#0284c7' }} />
                </IconButton>
                <IconButton 
                    size="small" 
                    onClick={onEdit}
                    sx={{ 
                        bgcolor: 'rgba(255,255,255,0.8)', 
                        border: '1px solid #e2e8f0', 
                        borderRadius: '6px',
                        p: '3px',
                        '&:hover': { bgcolor: '#f1f5f9' } 
                    }}
                >
                    <EditIcon sx={{ fontSize: '0.85rem', color: '#4f46e5' }} />
                </IconButton>
                <IconButton 
                    size="small" 
                    onClick={onDelete}
                    sx={{ 
                        bgcolor: 'rgba(255,255,255,0.8)', 
                        border: '1px solid #e2e8f0', 
                        borderRadius: '6px',
                        p: '3px',
                        '&:hover': { bgcolor: '#fee2e2', color: '#ef4444' } 
                    }}
                >
                    <DeleteIcon sx={{ fontSize: '0.85rem', color: '#ef4444' }} />
                </IconButton>
            </Box>

            {/* Icon representation */}
            <Box sx={{ 
                width: 32, 
                height: 32, 
                borderRadius: '50%', 
                bgcolor: style.iconBg, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                mb: 1.5 
            }}>
                <TableChartIcon sx={{ fontSize: '1.1rem', color: style.iconColor }} />
            </Box>
            {/* Value & Trend Row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                <Typography sx={{ fontSize: '28px', fontWeight: 'bold', color: '#1e293b', lineHeight: 1.2 }}>
                    {loading ? <Skeleton width="60px" /> : formattedValue}
                </Typography>
                {!loading && cardData && cardData.variance !== undefined && cardData.variance !== null && (
                    <Box sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.25,
                        px: 1,
                        py: 0.25,
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 700,
                        bgcolor: cardData.variance >= 0 ? 'rgba(220, 252, 231, 0.8)' : 'rgba(254, 226, 226, 0.8)',
                        color: cardData.variance >= 0 ? '#15803d' : '#b91c1c',
                        border: cardData.variance >= 0 ? '1px solid rgba(187, 247, 208, 0.9)' : '1px solid rgba(254, 202, 202, 0.9)',
                        backdropFilter: 'blur(4px)'
                    }}>
                        {cardData.variance >= 0 ? '▲' : '▼'} {Math.abs(cardData.variance).toFixed(1)}%
                    </Box>
                )}
            </Box>
 
            {/* Title / Label */}
            <Typography sx={{ fontSize: '12px', fontWeight: 'bold', color: '#334155', mt: 1 }}>
                {chart.title || style.title}
            </Typography>
 
            {/* Subtitle */}
            <Typography sx={{ fontSize: '10px', color: '#64748b', mt: 0.25 }}>
                {chart.comparisonEnabled && cardData && cardData.variance !== null ? `vs previous ${chart.comparisonPeriod || 'month'}` : style.subtitle}
            </Typography>
        </Card>
    );
};
